import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  startTransition,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { normalizePhoneNumber } from "../utils/phoneNumber";
import DueTimePicker from "../Components/DueTimePicker";
import {
  putFileToPresignedUrl,
  resolveFileContentType,
} from "../utils/s3Upload";

const MAX_OWNER_ATTACHMENTS = 10;
/** Keep Materials table responsive for large Excel imports (up to ~1000 lanes). */
const MATERIALS_PAGE_SIZE = 50;
const BOQ_LANES_PAGE_SIZE = 100;

const API_URL = process.env.REACT_APP_API_URL;

/** Presigned URL for iframe / Open PDF. Do not open private pdfLink directly. */
function getLrfqPdfPreviewUrl(payload) {
  if (!payload || typeof payload !== "object") return "";
  return String(
    payload.pdfDownloadUrl || payload.downloadUrl || "",
  ).trim();
}

const CURRENCY_OPTIONS = ["INR", "USD", "EUR"];
const FINAL_STATUS = "Active";
const DEFAULT_DUE_TIME = "20:00";
const DUE_DATE_OFFSET_DAYS = 2;
const TRUCK_BODY_OPTIONS = ["Open", "Closed"];
const TRUCK_BODY_LOOKUP = TRUCK_BODY_OPTIONS.reduce((map, body) => {
  map[body.toLowerCase()] = body;
  return map;
}, {});
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NUMBER_REGEX = /^\d*\.?\d*$/;

const emptyLane = (lineNo, laneIndex) => ({
  uniqueId: "",
  sNo: `${lineNo}.${laneIndex + 1}`,
  sourcePincode: "",
  destinationPincode: "",
  sourceCity: "",
  destinationCity: "",
  truckName: "",
  truckBody: "",
  truckLoadabilityMt: "",
  truckLengthFt: "",
  truckWidthFt: "",
  truckHeightFt: "",
  quantity: ''
});

const emptyMaterial = () => ({
  uniqueId: "",
  sourceCode: "",
  sourceDescription: "",
  hsn: "",
  uom: "",
  quantity: "",
  boq: [],
});

const emptyTerm = () => ({ rfqTerm: "", termsResponse: "" });

const DEFAULT_TERMS = [
  {
    rfqTerm: "Payment Terms",
    termsResponse: "30 days from the receipt of material",
  },
  { rfqTerm: "Freight Terms", termsResponse: "Please specify if any" },
  { rfqTerm: "Transit Time", termsResponse: "Please specify if any" },
  { rfqTerm: "Insurance", termsResponse: "Please specify if any" },
  { rfqTerm: "Special Instructions", termsResponse: "Please specify if any" },
];
const getDefaultTerms = () => DEFAULT_TERMS.map((t) => ({ ...t }));

const emptySupplier = () => ({
  supplierId: "",
  supplierName: "",
  supplierMailId: "",
  phoneNumber: "",
  contactPerson: "",
  address: "",
  gstNumber: "",
  gstStatus: "",
  gstLegalName: "",
  gstTradeName: "",
  gstConstitutionType: "",
  gstTaxPayerType: "",
  gstRegistrationDate: "",
  gstState: "",
  city: "",
  state: "",
  emailLink: "",
  /** True only after send-lrfq-email (not merely having a portal emailLink from Activate). */
  emailSent: false,
});

const mapOwnerFromApi = (item) => ({
  s3Key: item?.s3Key || "",
  originalFileName: item?.originalFileName || "",
  contentType: item?.contentType || "",
  uploadedAt: item?.uploadedAt || "",
  url: item?.url || "",
});

function formatFileSize(bytes) {
  const size = Number(bytes);
  if (bytes == null || Number.isNaN(size)) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const isValidEmailAddress = (v) => EMAIL_REGEX.test(String(v || "").trim());

const parseAdditionalCcEmails = (value) =>
  String(value || "")
    .split(/[,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);

function buildCcEmails(userEmail, additionalInput) {
  const result = [];
  const seen = new Set();
  const add = (email) => {
    const t = String(email || "").trim();
    if (!t || seen.has(t.toLowerCase())) return;
    seen.add(t.toLowerCase());
    result.push(t);
  };
  add(userEmail);
  parseAdditionalCcEmails(additionalInput).forEach(add);
  return result;
}

/**
 * Like RFQ: Activate stores emailLink for Copy/View, but that is not "mail sent".
 * Only mark emailed after a successful send-lrfq-email (emailSent).
 */
const supplierAlreadyEmailed = (s) => Boolean(s?.emailSent);

/** Local/prod supplier portal URL (same idea as RfqView getSupplierEmailLink fallback). */
function buildLrfqSupplierEmailLink(lrfqId, supplierMailId, supplierId) {
  const id = String(lrfqId || "").trim();
  const mail = String(supplierMailId || "").trim();
  if (!id || !mail) return "";
  const sid = String(supplierId || "").trim();
  const base = `${window.location.origin}/lrfqSupplierView/${id}/${encodeURIComponent(mail)}`;
  return sid ? `${base}/${encodeURIComponent(sid)}` : base;
}

const str = (v) => (v == null ? "" : String(v));

const renumberLanes = (lanes, lineNo) =>
  lanes.map((lane, i) => ({ ...lane, sNo: `${lineNo}.${i + 1}` }));

const mapLaneFromApi = (l, lineNo, i) => ({
  uniqueId: str(l.uniqueId),
  sNo: l.sNo || `${lineNo}.${i + 1}`,
  sourcePincode: str(l.sourcePincode),
  destinationPincode: str(l.destinationPincode),
  sourceCity: str(l.sourceCity),
  destinationCity: str(l.destinationCity),
  truckName: str(l.truckName),
  truckBody: str(l.truckBody),
  truckLoadabilityMt: str(l.truckLoadabilityMt),
  truckLengthFt: str(l.truckLengthFt),
  truckWidthFt: str(l.truckWidthFt),
  truckHeightFt: str(l.truckHeightFt),
  quantity: str(l.quantity)
});

const mapMaterialFromApi = (m, index) => ({
  uniqueId: str(m.uniqueId),
  sourceCode: str(m.sourceCode),
  sourceDescription: str(m.sourceDescription),
  hsn: str(m.hsn || m.hsnCode),
  uom: str(m.uom),
  quantity: str(m.quantity),
  boq: (Array.isArray(m.boq) ? m.boq : []).map((l, i) =>
    mapLaneFromApi(l, index + 1, i),
  ),
});

const mapSupplierFromApi = (s, { mailSent = false } = {}) => {
  const emailLink = str(s?.emailLink || s?.quoteLink);
  const explicitSent = Boolean(s?.emailSent ?? s?.isEmailed);
  return {
    supplierId: str(s?.supplierId),
    supplierName: str(s?.supplierName),
    supplierMailId: str(s?.supplierMailId),
    phoneNumber: str(s?.phoneNumber || s?.phonenumber),
    contactPerson: str(s?.contactPerson),
    address: str(s?.address),
    gstNumber: str(s?.gstNumber),
    gstStatus: str(s?.gstStatus),
    gstLegalName: str(s?.gstLegalName),
    gstTradeName: str(s?.gstTradeName),
    gstConstitutionType: str(s?.gstConstitutionType),
    gstTaxPayerType: str(s?.gstTaxPayerType),
    gstRegistrationDate: str(s?.gstRegistrationDate),
    gstState: str(s?.gstState),
    city: str(s?.city),
    state: str(s?.state),
    emailLink,
    // emailLink may exist after Activate; emailSent only after real send / isMailSent.
    emailSent: explicitSent || (mailSent && Boolean(emailLink)),
  };
};

function parseApiResponse(raw) {
  let d = raw;
  if (typeof d === "string") {
    try {
      d = JSON.parse(d);
    } catch {
      return raw;
    }
  }
  if (d?.body != null) {
    if (typeof d.body === "string") {
      try {
        return JSON.parse(d.body);
      } catch {
        return d.body;
      }
    }
    return d.body;
  }
  return d;
}

function isApiSuccess(r) {
  if (!r || typeof r !== "object") return false;
  const s = r.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

const pad = (n) => String(n).padStart(2, "0");

function getTodayIsoDate() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function addDaysToIsoDate(isoDate, days) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-").map(Number);
  if (!yyyy || !mm || !dd) return "";
  const date = new Date(yyyy, mm - 1, dd);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatDateForApi(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}-${mm}-${yyyy}`;
}

function formatDateTimeForApi(isoDate, time) {
  const d = formatDateForApi(isoDate);
  if (!d) return "";
  return `${d} ${String(time || DEFAULT_DUE_TIME)
    .trim()
    .slice(0, 5)}`;
}

function parseDateFromApi(value) {
  if (!value) return "";
  const s = String(value).trim();
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function parseTimeFromApi(value) {
  if (!value) return "";
  const m = String(value)
    .trim()
    .match(/^\d{2}-\d{2}-\d{4}[ T](\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

function isDueBeforeLrfqDate(lrfqDate, dueDate, dueTime) {
  if (!lrfqDate || !dueDate) return false;
  const due = new Date(`${dueDate}T${dueTime || DEFAULT_DUE_TIME}`);
  const start = new Date(`${lrfqDate}T00:00`);
  if (Number.isNaN(due.getTime()) || Number.isNaN(start.getTime()))
    return false;
  return due < start;
}

const isLaneEmpty = (l) =>
  !l.sourcePincode.trim() &&
  !l.destinationPincode.trim() &&
  !l.sourceCity.trim() &&
  !l.destinationCity.trim() &&
  !l.truckName.trim() &&
  !l.truckBody;

const isMaterialEmpty = (m) =>
  !m.sourceCode.trim() &&
  !m.sourceDescription.trim() &&
  !m.hsn.trim() &&
  !m.uom.trim() &&
  m.quantity === "" &&
  m.boq.every(isLaneEmpty);

function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return t;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return null;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (raw.fileBase64 != null) return extractExcelBase64(raw.fileBase64);
    if (raw.file != null) return extractExcelBase64(raw.file);
    if (raw.base64 != null) return extractExcelBase64(raw.base64);
    if (raw.data != null) return extractExcelBase64(raw.data);
  }
  return null;
}

function downloadXlsxFromBase64(
  base64,
  fileName = "LRFQ-Materials-Lanes-Sample.xlsx",
) {
  const chars = atob(base64);
  const bytes = new Uint8Array(chars.length);
  for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const MAX_IMPORT_FILE_SIZE = 5 * 1024 * 1024;

const LogisticsRfqCreate = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();

  const [activeTab, setActiveTab] = useState("details");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNumber, setIsFetchingNumber] = useState(!editId);
  const [isLoadingLrfq, setIsLoadingLrfq] = useState(Boolean(editId));
  const [lrfqId, setLrfqId] = useState(editId || null);
  const [recordStatus, setRecordStatus] = useState("");
  const [pdfLink, setPdfLink] = useState("");
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState("");
  const [isMailSent, setIsMailSent] = useState(false);
  const [showPreviewStep, setShowPreviewStep] = useState(false);

  const [ownerAttachments, setOwnerAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const attachmentInputRef = useRef(null);
  // Per-file mandatory PDF upload: Set of s3Keys (saved files) that require supplier to upload signed PDF
  const [mandatoryPdfKeys, setMandatoryPdfKeys] = useState(() => new Set());
  // For pending files (not yet uploaded, no s3Key): track by file name
  const [mandatoryPdfFileNames, setMandatoryPdfFileNames] = useState(() => new Set());

  const [userEmail, setUserEmail] = useState("");
  const [additionalCcEmails, setAdditionalCcEmails] = useState("");
  const [ccUserSuggestions, setCcUserSuggestions] = useState([]);
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const ccDebounceRef = useRef(null);
  const ccRequestIdRef = useRef(0);

  const [details, setDetails] = useState(() => {
    const today = getTodayIsoDate();
    return {
      lrfqNumber: "",
      lrfqDate: today,
      lrfqDueDate: addDaysToIsoDate(today, DUE_DATE_OFFSET_DAYS),
      lrfqDueTime: DEFAULT_DUE_TIME,
      lrfqSummary: "",
      lrfqCurrency: "INR",
    };
  });
  const [terms, setTerms] = useState(getDefaultTerms);
  const [materials, setMaterials] = useState([emptyMaterial()]);
  const [suppliers, setSuppliers] = useState([emptySupplier()]);

  const [unitOptions, setUnitOptions] = useState([]);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);

  const [supplierSearch, setSupplierSearch] = useState("");
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSearchResults, setSupplierSearchResults] = useState([]);
  const supplierSearchDebounceRef = useRef(null);
  const supplierSearchRequestIdRef = useRef(0);
  const [activeMaterialRowIndex, setActiveMaterialRowIndex] = useState(0);
  /** BOQ lane grids stay collapsed after large imports — expand one material at a time. */
  const [expandedBoqIndexes, setExpandedBoqIndexes] = useState(() => new Set());
  const [boqLanePageByMaterial, setBoqLanePageByMaterial] = useState({});
  const [materialsPage, setMaterialsPage] = useState(0);

  const [materialDeleteIndex, setMaterialDeleteIndex] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const importFileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDownloadingSample, setIsDownloadingSample] = useState(false);
  const [importMeta, setImportMeta] = useState({
    sourceCode: "",
    sourceDescription: "",
    hsn: "",
    uom: "",
  });

  const isActive =
    String(recordStatus || "").toLowerCase() === FINAL_STATUS.toLowerCase();

  const canAccessPreview = isActive || showPreviewStep;

  const loadLrfqForEdit = useCallback(async () => {
    if (!editId) return;
    setIsLoadingLrfq(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-lrfq-by-id",
        lrfqid: editId,
      });
      const responseData = parseApiResponse(response.data);
      const lrfq = responseData?.data;

      if (!isApiSuccess(responseData) || !lrfq || typeof lrfq !== "object") {
        toast.error(responseData?.message || "Failed to load Logistics RFQ", {
          autoClose: 2000,
        });
        navigate("/logisticsRfq");
        return;
      }

      setLrfqId(lrfq._id || lrfq.id || editId);
      setRecordStatus(lrfq.status || "");
      setIsMailSent(Boolean(lrfq.isMailSent));
      setPdfLink(lrfq.pdfLink || "");
      setPdfDownloadUrl(getLrfqPdfPreviewUrl(lrfq));
      setOwnerAttachments(
        Array.isArray(lrfq.lrfqOwner)
          ? lrfq.lrfqOwner.map(mapOwnerFromApi)
          : [],
      );
      // Load per-file mandatory keys from API response
      setMandatoryPdfKeys(
        new Set(
          Array.isArray(lrfq.requiresPdfUploadKeys)
            ? lrfq.requiresPdfUploadKeys
            : lrfq.requiresPdfUpload
              ? (Array.isArray(lrfq.lrfqOwner) ? lrfq.lrfqOwner.map((f) => f?.s3Key).filter(Boolean) : [])
              : [],
        ),
      );
      setMandatoryPdfFileNames(new Set());
      setPendingFiles([]);
      if (
        String(lrfq.status || "").toLowerCase() === FINAL_STATUS.toLowerCase()
      ) {
        setShowPreviewStep(true);
        setActiveTab("preview");
      }
      setDetails({
        lrfqNumber: lrfq.lrfqNumber || lrfq.LrfqNumber || "",
        lrfqDate: parseDateFromApi(lrfq.lrfqDate),
        lrfqDueDate: parseDateFromApi(lrfq.lrfqDueDate),
        lrfqDueTime: parseTimeFromApi(lrfq.lrfqDueDate) || DEFAULT_DUE_TIME,
        lrfqSummary: lrfq.lrfqSummary || "",
        lrfqCurrency: lrfq.lrfqCurrency || "INR",
      });

      setTerms(
        Array.isArray(lrfq.terms) && lrfq.terms.length > 0
          ? lrfq.terms.map((t) => ({
              rfqTerm: t.rfqTerm || "",
              termsResponse: t.termsResponse || "",
            }))
          : getDefaultTerms(),
      );

      setMaterials(
        Array.isArray(lrfq.materials) && lrfq.materials.length > 0
          ? lrfq.materials.map(mapMaterialFromApi)
          : [emptyMaterial()],
      );
      setExpandedBoqIndexes(new Set());
      setBoqLanePageByMaterial({});
      setMaterialsPage(0);

      setSuppliers(
        Array.isArray(lrfq.suppliers) && lrfq.suppliers.length > 0
          ? lrfq.suppliers.map((s) =>
              mapSupplierFromApi(s, { mailSent: Boolean(lrfq.isMailSent) }),
            )
          : [emptySupplier()],
      );
    } catch (error) {
      console.error("Load Logistics RFQ error:", error);
      toast.error("Failed to load Logistics RFQ", { autoClose: 2000 });
      navigate("/logisticsRfq");
    } finally {
      setIsLoadingLrfq(false);
    }
  }, [editId, navigate]);

  const fetchNextLrfqNumber = async () => {
    setIsFetchingNumber(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-next-lrfq-number",
      });
      const responseData = parseApiResponse(response.data);
      const next =
        responseData?.data?.nextSequenceNumber ||
        responseData?.data?.lrfqNumber;
      if (isApiSuccess(responseData) && next) {
        setDetails((prev) => ({ ...prev, lrfqNumber: next }));
      } else {
        toast.error(responseData?.message || "Failed to fetch LRFQ number", {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Fetch next LRFQ number error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to fetch next LRFQ number", {
        autoClose: 2000,
      });
    } finally {
      setIsFetchingNumber(false);
    }
  };

  const fetchUnits = async () => {
    setIsLoadingUnits(true);
    try {
      const response = await axios.post(API_URL, {
        action: "get-all-units",
        page: 1,
        limit: 500,
        sortBy: "unit",
        sortOrder: "asc",
      });
      const responseData = parseApiResponse(response.data);
      const list = Array.isArray(responseData?.data?.units)
        ? responseData.data.units
        : [];
      const active = list
        .map((item) => ({
          id: item?._id || item?.id || "",
          unit: String(item?.unit || "").trim(),
          status: String(item?.status || "").trim(),
        }))
        .filter((i) => i.unit)
        .filter((i) => !i.status || i.status.toLowerCase() === "active");
      setUnitOptions(isApiSuccess(responseData) ? active : []);
    } catch (err) {
      console.error("Fetch units error:", err);
      setUnitOptions([]);
    } finally {
      setIsLoadingUnits(false);
    }
  };

  useEffect(() => {
    if (editId) loadLrfqForEdit();
    else fetchNextLrfqNumber();
  }, [editId, loadLrfqForEdit]);

  useEffect(() => {
    fetchUnits();
  }, []);

  const refreshLrfqById = useCallback(async () => {
    if (!lrfqId) return null;
    try {
      const response = await axios.post(API_URL, {
        action: "get-lrfq-by-id",
        lrfqid: lrfqId,
      });
      const responseData = parseApiResponse(response.data);
      const lrfq = responseData?.data;
      if (!isApiSuccess(responseData) || !lrfq) return null;

      setIsMailSent(Boolean(lrfq.isMailSent));
      if (lrfq.pdfLink) setPdfLink(lrfq.pdfLink);
      const previewUrl = getLrfqPdfPreviewUrl(lrfq);
      if (previewUrl) setPdfDownloadUrl(previewUrl);
      if (Array.isArray(lrfq.lrfqOwner)) {
        setOwnerAttachments(lrfq.lrfqOwner.map(mapOwnerFromApi));
      }
      if (Array.isArray(lrfq.suppliers) && lrfq.suppliers.length > 0) {
        const mailSent = Boolean(lrfq.isMailSent);
        const apiByMail = new Map(
          lrfq.suppliers.map((s) => [
            String(s.supplierMailId || "")
              .trim()
              .toLowerCase(),
            mapSupplierFromApi(s, { mailSent }),
          ]),
        );
        setSuppliers((prev) =>
          prev.map((row) => {
            const fromApi = apiByMail.get(
              row.supplierMailId.trim().toLowerCase(),
            );
            if (!fromApi) return row;
            return {
              ...row,
              supplierId: fromApi.supplierId || row.supplierId,
              emailLink: fromApi.emailLink || row.emailLink,
              // Keep local emailSent after Send Email; API may omit the flag on update.
              emailSent: Boolean(fromApi.emailSent || row.emailSent),
            };
          }),
        );
      }
      return lrfq;
    } catch (error) {
      console.error("Refresh LRFQ error:", error);
      return null;
    }
  }, [lrfqId]);

  useEffect(() => {
    const userId = localStorage.getItem("id");
    if (!userId) return;
    (async () => {
      try {
        const response = await axios.post(API_URL, {
          action: "get-user",
          id: userId,
        });
        const data = parseApiResponse(response.data);
        if (isApiSuccess(data) && data.user?.email) {
          setUserEmail(String(data.user.email).trim());
        }
      } catch (error) {
        console.error("Fetch user email error:", error);
      }
    })();
  }, []);

  const getCcSearchTerm = (value) => {
    const parts = String(value || "").split(/[,;]/);
    return parts[parts.length - 1].trim();
  };

  const searchCcUsers = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: "get-users",
        page: 1,
        limit: 25,
        sortBy: "createdAt",
        sortOrder: "desc",
        search: term,
      });
      if (requestId !== ccRequestIdRef.current) return;
      const data = parseApiResponse(response.data);
      const users = Array.isArray(data?.users) ? data.users : [];
      setCcUserSuggestions(users.filter((u) => isValidEmailAddress(u?.email)));
    } catch (error) {
      console.error("Search CC users error:", error);
      if (requestId === ccRequestIdRef.current) setCcUserSuggestions([]);
    } finally {
      if (requestId === ccRequestIdRef.current) setIsSearchingCcUsers(false);
    }
  };

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value;
    setAdditionalCcEmails(value);
    ccRequestIdRef.current += 1;
    const requestId = ccRequestIdRef.current;
    if (ccDebounceRef.current) clearTimeout(ccDebounceRef.current);

    const term = getCcSearchTerm(value);
    if (term.length < 3) {
      setCcUserSuggestions([]);
      setIsSearchingCcUsers(false);
      return;
    }
    setIsSearchingCcUsers(true);
    ccDebounceRef.current = setTimeout(
      () => searchCcUsers(term, requestId),
      400,
    );
  };

  const handleSelectCcUser = (email) => {
    const selected = String(email || "").trim();
    if (!selected) return;
    setAdditionalCcEmails((prev) => {
      const value = String(prev || "");
      const lastComma = Math.max(
        value.lastIndexOf(","),
        value.lastIndexOf(";"),
      );
      const base = lastComma >= 0 ? `${value.slice(0, lastComma + 1)} ` : "";
      if (
        parseAdditionalCcEmails(base).some(
          (i) => i.toLowerCase() === selected.toLowerCase(),
        )
      ) {
        return base.trimStart();
      }
      return `${base}${selected}, `;
    });
    ccRequestIdRef.current += 1;
    setCcUserSuggestions([]);
    setIsSearchingCcUsers(false);
  };

  const handleAdditionalCcBlur = () => {
    setTimeout(() => {
      setCcUserSuggestions([]);
      setIsSearchingCcUsers(false);
    }, 180);
  };

  useEffect(
    () => () => {
      if (supplierSearchDebounceRef.current) {
        clearTimeout(supplierSearchDebounceRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!window.$) return undefined;
    const $modal = window.$("#modalDeleteLrfqMaterial");
    const onHidden = () => setMaterialDeleteIndex(null);
    $modal.on("hidden.bs.modal", onHidden);
    return () => $modal.off("hidden.bs.modal", onHidden);
  }, []);

  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    if (name === "lrfqNumber") return;
    setDetails((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "lrfqDate") {
        next.lrfqDueDate = value
          ? addDaysToIsoDate(value, DUE_DATE_OFFSET_DAYS)
          : "";
      }
      return next;
    });
  };

  const updateTerm = (index, field, value) =>
    setTerms((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const addTermRowAfter = (index) =>
    setTerms((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptyTerm());
      return next;
    });

  const removeTermRow = (index) => {
    if (terms.length <= 1) {
      setTerms([emptyTerm()]);
      return;
    }
    setTerms((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMaterial = (index, field, value) =>
    setMaterials((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const handleQuantityChange = (index, value) => {
    if (value === "" || NUMBER_REGEX.test(value)) {
      updateMaterial(index, "quantity", value);
    }
  };

  const addMaterialRowAfter = (index) =>
    setMaterials((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptyMaterial());
      return next;
    });

  const handleMaterialDeleteClick = (index) => {
    setMaterialDeleteIndex(index);
    if (window.$) window.$("#modalDeleteLrfqMaterial").modal("show");
  };

  const handleConfirmDeleteMaterial = () => {
    if (materialDeleteIndex == null) return;
    const index = materialDeleteIndex;
    setMaterials((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0
        ? next.map((m, i) => ({ ...m, boq: renumberLanes(m.boq, i + 1) }))
        : [emptyMaterial()];
    });
    if (window.$) window.$("#modalDeleteLrfqMaterial").modal("hide");
    setMaterialDeleteIndex(null);
  };

  const getMaterialLabel = (row) =>
    row?.sourceDescription?.trim() || row?.sourceCode?.trim() || "-";

  const toggleBoqExpand = (mIndex) => {
    setExpandedBoqIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(mIndex)) next.delete(mIndex);
      else next.add(mIndex);
      return next;
    });
  };

  const addLane = (mIndex) => {
    setMaterials((prev) =>
      prev.map((row, i) =>
        i === mIndex
          ? {
              ...row,
              boq: [...row.boq, emptyLane(mIndex + 1, row.boq.length)],
            }
          : row,
      ),
    );
    setExpandedBoqIndexes((prev) => {
      const next = new Set(prev);
      next.add(mIndex);
      return next;
    });
  };

  const updateLane = (mIndex, lIndex, field, value) =>
    setMaterials((prev) =>
      prev.map((row, i) =>
        i === mIndex
          ? {
              ...row,
              boq: row.boq.map((lane, li) =>
                li === lIndex ? { ...lane, [field]: value } : lane,
              ),
            }
          : row,
      ),
    );

  const handleLanePincode = (mIndex, lIndex, field, value) => {
    if (/^\d{0,6}$/.test(value)) updateLane(mIndex, lIndex, field, value);
  };

  const handleLaneNumber = (mIndex, lIndex, field, value) => {
    if (value === "" || NUMBER_REGEX.test(value)) {
      updateLane(mIndex, lIndex, field, value);
    }
  };

  const removeLane = (mIndex, lIndex) =>
    setMaterials((prev) =>
      prev.map((row, i) =>
        i === mIndex
          ? {
              ...row,
              boq: renumberLanes(
                row.boq.filter((_, li) => li !== lIndex),
                mIndex + 1,
              ),
            }
          : row,
      ),
    );

  const closeImportModal = () => {
    if (isImporting) return;
    setShowImportModal(false);
    setImportFile(null);
    setIsDragOver(false);
    setImportMeta({
      sourceCode: "",
      sourceDescription: "",
      hsn: "",
      uom: "KGS",
    });
    if (importFileInputRef.current) importFileInputRef.current.value = "";
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select an .xlsx or .xls file", { autoClose: 2000 });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.error("File size must be 5 MB or less", { autoClose: 2000 });
      e.target.value = "";
      return;
    }
    setImportFile(file);
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isImporting) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please select an .xlsx or .xls file", { autoClose: 2000 });
      return;
    }
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      toast.error("File size must be 5 MB or less", { autoClose: 2000 });
      return;
    }
    setImportFile(file);
  };

  const removeImportFile = () => {
    if (isImporting) return;
    setImportFile(null);
    if (importFileInputRef.current) importFileInputRef.current.value = "";
  };

  const SUPPLIER_SEARCH_MIN_LENGTH = 3;
  const SUPPLIER_SEARCH_DEBOUNCE_MS = 400;

  const updateSupplier = (index, field, value) =>
    setSuppliers((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, [field]: value };
        if (field === "supplierMailId") {
          next.supplierId = "";
          next.emailLink = "";
          next.emailSent = false;
        }
        return next;
      }),
    );

  const addSupplierRowAfter = (index) =>
    setSuppliers((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, emptySupplier());
      return next;
    });

  const removeSupplierRow = (index) => {
    if (suppliers.length <= 1) {
      setSuppliers([emptySupplier()]);
      return;
    }
    setSuppliers((prev) => prev.filter((_, i) => i !== index));
  };

  const isSupplierAlreadyAdded = (email) => {
    const e = String(email || "")
      .trim()
      .toLowerCase();
    return (
      Boolean(e) &&
      suppliers.some((s) => s.supplierMailId.trim().toLowerCase() === e)
    );
  };

  const searchSuppliers = useCallback(
    async (searchTerm, { showToast = true } = {}) => {
      const search = String(searchTerm ?? "").trim();
      if (!search) {
        setSupplierSearchResults([]);
        if (showToast) {
          toast.error("Enter supplier name or email to search", {
            autoClose: 2000,
          });
        }
        return;
      }
      if (search.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        setSupplierSearchResults([]);
        return;
      }

      const requestId = ++supplierSearchRequestIdRef.current;
      setSupplierSearchLoading(true);
      try {
        const userId = localStorage.getItem("id") || "";
        const pageLimit = 100;
        const mergeUnique = (target, incoming) => {
          const seen = new Set(
            target.map((s) => String(s.id || s._id || "")).filter(Boolean),
          );
          incoming.forEach((s) => {
            const key = String(s.id || s._id || "");
            if (key && seen.has(key)) return;
            if (key) seen.add(key);
            target.push(s);
          });
          return target;
        };

        const fetchPage = async (page) => {
          const payload = {
            action: "get-suppliers",
            status: "active",
            search,
            page,
            limit: pageLimit,
          };
          if (userId) payload.currentUserId = userId;
          const response = await axios.post(API_URL, payload);
          return parseApiResponse(response.data);
        };

        // API defaults to limit 10 — page through until all matches are loaded.
        let page = 1;
        let all = [];
        let firstResponse = null;
        let guard = 0;
        while (guard < 50) {
          guard += 1;
          const responseData = await fetchPage(page);
          if (requestId !== supplierSearchRequestIdRef.current) return;
          if (!firstResponse) firstResponse = responseData;

          if (!isApiSuccess(responseData)) {
            setSupplierSearchResults([]);
            if (showToast) {
              toast.info(responseData?.message || "No suppliers found", {
                autoClose: 2000,
              });
            }
            return;
          }

          const list =
            responseData?.suppliers || responseData?.data?.suppliers || [];
          const beforeCount = all.length;
          if (Array.isArray(list) && list.length > 0) {
            mergeUnique(all, list);
          }
          const addedCount = all.length - beforeCount;

          const pagination =
            responseData?.pagination || responseData?.data?.pagination || {};
          const totalPages = Number(pagination.totalPages) || 0;
          const totalCount = Number(pagination.totalCount) || 0;
          const hasNext =
            pagination.hasNextPage === true ||
            (totalPages > 0 && page < totalPages);

          if (
            !hasNext ||
            !Array.isArray(list) ||
            list.length === 0 ||
            addedCount === 0 ||
            (totalCount > 0 && all.length >= totalCount)
          ) {
            break;
          }
          page += 1;
          if (totalPages > 0 && page > totalPages) break;
        }

        if (all.length > 0) {
          setSupplierSearchResults(all);
          if (showToast) {
            toast.info(`${all.length} supplier(s) found.`, {
              autoClose: 2500,
            });
          }
        } else {
          setSupplierSearchResults([]);
          if (showToast) {
            toast.info(firstResponse?.message || "No suppliers found", {
              autoClose: 2000,
            });
          }
        }
      } catch (error) {
        if (requestId !== supplierSearchRequestIdRef.current) return;
        console.error("Supplier search error:", error);
        const errData = error.response?.data
          ? parseApiResponse(error.response.data)
          : null;
        toast.error(errData?.message || "Failed to search suppliers", {
          autoClose: 2000,
        });
        setSupplierSearchResults([]);
      } finally {
        if (requestId === supplierSearchRequestIdRef.current) {
          setSupplierSearchLoading(false);
        }
      }
    },
    [],
  );

  const handleSupplierSearchChange = (e) => {
    const value = e.target.value;
    setSupplierSearch(value);
    supplierSearchRequestIdRef.current += 1;
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }
    const trimmed = value.trim();
    if (trimmed.length < SUPPLIER_SEARCH_MIN_LENGTH) {
      setSupplierSearchLoading(false);
      setSupplierSearchResults([]);
      return;
    }
    supplierSearchDebounceRef.current = setTimeout(() => {
      searchSuppliers(trimmed, { showToast: false });
    }, SUPPLIER_SEARCH_DEBOUNCE_MS);
  };

  const handleSupplierSearch = () => {
    if (supplierSearchDebounceRef.current) {
      clearTimeout(supplierSearchDebounceRef.current);
    }
    searchSuppliers(supplierSearch, { showToast: true });
  };

  const handleSupplierSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSupplierSearch();
    }
  };

  const selectSupplier = (supplier) => {
    const email = (supplier.email || "").trim();
    if (!email) {
      toast.error("This supplier has no email on record", { autoClose: 2000 });
      return;
    }
    if (isSupplierAlreadyAdded(email)) {
      toast.info("Supplier already added", { autoClose: 2000 });
      return;
    }
    const row = {
      supplierId: String(supplier._id || supplier.id || "").trim(),
      supplierName: (supplier.clientname || "").trim(),
      supplierMailId: email,
      phoneNumber: String(
        supplier.phonenumber || supplier.phoneNumber || "",
      ).trim(),
      contactPerson: String(supplier.contactPerson || "").trim(),
      address: String(supplier.address || "").trim(),
      gstNumber: String(supplier.gstNumber || "").trim(),
      gstStatus: String(supplier.gstStatus || "").trim(),
      gstLegalName: String(supplier.gstLegalName || "").trim(),
      gstTradeName: String(supplier.gstTradeName || "").trim(),
      gstConstitutionType: String(supplier.gstConstitutionType || "").trim(),
      gstTaxPayerType: String(supplier.gstTaxPayerType || "").trim(),
      gstRegistrationDate: String(supplier.gstRegistrationDate || "").trim(),
      gstState: String(supplier.gstState || "").trim(),
      city: String(supplier.city || "").trim(),
      state: String(supplier.state || "").trim(),
      emailLink: "",
      emailSent: false,
    };
    setSuppliers((prev) => {
      const emptyIdx = prev.findIndex(
        (s) => !s.supplierName.trim() && !s.supplierMailId.trim(),
      );
      if (emptyIdx === -1) return [...prev, row];
      const next = prev.map((s, i) => (i === emptyIdx ? row : s));
      const hasEmpty = next.some(
        (s) => !s.supplierName.trim() && !s.supplierMailId.trim(),
      );
      return hasEmpty ? next : [...next, emptySupplier()];
    });
    toast.success("Supplier added", { autoClose: 1500 });
  };

  const validateDetails = () => {
    if (isFetchingNumber) {
      toast.error("LRFQ number is still loading. Please wait.", {
        autoClose: 2000,
      });
      return false;
    }
    if (!details.lrfqNumber.trim()) {
      toast.error("LRFQ number is required", { autoClose: 2000 });
      return false;
    }
    if (!details.lrfqDate) {
      toast.error("LRFQ date is required", { autoClose: 2000 });
      return false;
    }
    if (!details.lrfqDueDate) {
      toast.error("LRFQ due date is required", { autoClose: 2000 });
      return false;
    }
    if (!details.lrfqDueTime) {
      toast.error("LRFQ due time is required", { autoClose: 2000 });
      return false;
    }
    if (
      isDueBeforeLrfqDate(
        details.lrfqDate,
        details.lrfqDueDate,
        details.lrfqDueTime,
      )
    ) {
      toast.error("Due date and time cannot be before LRFQ date", {
        autoClose: 2000,
      });
      return false;
    }
    if (!details.lrfqSummary.trim()) {
      toast.error("LRFQ summary is required", { autoClose: 2000 });
      return false;
    }
    if (!details.lrfqCurrency.trim()) {
      toast.error("Currency is required", { autoClose: 2000 });
      return false;
    }
    return true;
  };

  const validateMaterials = () => {
    const filled = materials
      .map((m, index) => ({ m, index }))
      .filter(({ m }) => !isMaterialEmpty(m));

    if (filled.length === 0) {
      toast.error("Add at least one material line", { autoClose: 2000 });
      return false;
    }

    for (const { m, index } of filled) {
      const n = index + 1;
      if (!m.sourceDescription.trim()) {
        toast.error(`Material ${n}: Source description is required`, {
          autoClose: 2000,
        });
        return false;
      }
      if (m.quantity === "" || Number.isNaN(parseFloat(m.quantity))) {
        toast.error(`Material ${n}: Valid quantity is required`, {
          autoClose: 2000,
        });
        return false;
      }
      const lanes = m.boq.filter((l) => !isLaneEmpty(l));
      if (lanes.length === 0) {
        toast.error(`Material ${n}: Add at least one lane (truck line)`, {
          autoClose: 2000,
        });
        return false;
      }
      for (const lane of lanes) {
        const label = `Material ${n} / ${lane.sNo}`;
        if (!/^\d{6}$/.test(lane.sourcePincode)) {
          toast.error(`${label}: Source pincode must be 6 digits`, {
            autoClose: 2000,
          });
          return false;
        }
        if (!/^\d{6}$/.test(lane.destinationPincode)) {
          toast.error(`${label}: Destination pincode must be 6 digits`, {
            autoClose: 2000,
          });
          return false;
        }
        if (!lane.sourceCity.trim() || !lane.destinationCity.trim()) {
          toast.error(`${label}: Source and destination city are required`, {
            autoClose: 2000,
          });
          return false;
        }
        if (!lane.truckName.trim()) {
          toast.error(`${label}: Truck name is required`, { autoClose: 2000 });
          return false;
        }
        if (!lane.truckBody) {
          toast.error(`${label}: Select truck body (Open/Closed)`, {
            autoClose: 2000,
          });
          return false;
        }
      }
    }
    return true;
  };

  const validateSuppliers = () => {
    const filled = suppliers.filter(
      (s) => s.supplierName.trim() && s.supplierMailId.trim(),
    );
    if (filled.length === 0) {
      toast.error("Add at least one supplier with name and email", {
        autoClose: 2000,
      });
      return false;
    }
    for (let i = 0; i < suppliers.length; i++) {
      const row = suppliers[i];
      if (
        (row.supplierName.trim() || row.supplierMailId.trim()) &&
        !EMAIL_REGEX.test(row.supplierMailId.trim())
      ) {
        toast.error(`Supplier row ${i + 1}: Enter a valid email`, {
          autoClose: 2000,
        });
        return false;
      }
    }
    return true;
  };

  const buildTermsPayload = () =>
    terms
      .filter((t) => t.rfqTerm.trim())
      .map((t) => ({
        rfqTerm: t.rfqTerm.trim(),
        termsResponse: (t.termsResponse || "").trim(),
      }));

  const num = (v) => parseFloat(v) || 0;

  const buildMaterialsPayload = () =>
    materials
      .filter((m) => !isMaterialEmpty(m))
      .map((m, mIndex) => {
        const lanes = m.boq.filter((l) => !isLaneEmpty(l));
        const row = {
          sourceCode: m.sourceCode.trim(),
          sourceDescription: m.sourceDescription.trim(),
          hsn: m.hsn.trim(),
          uom: m.uom.trim(),
          quantity: num(m.quantity),
          isBoq: lanes.length > 0,
          boq: lanes.map((l, lIndex) => {
            const lane = {
              sNo: l.sNo || `${mIndex + 1}.${lIndex + 1}`,
              sourcePincode: l.sourcePincode.trim(),
              destinationPincode: l.destinationPincode.trim(),
              sourceCity: l.sourceCity.trim(),
              destinationCity: l.destinationCity.trim(),
              truckName: l.truckName.trim(),
              truckBody: l.truckBody,
              truckLoadabilityMt: l.truckLoadabilityMt.trim(),
              truckLengthFt: num(l.truckLengthFt),
              truckWidthFt: num(l.truckWidthFt),
              truckHeightFt: num(l.truckHeightFt),
              quantity: num(l.quantity)
            };
            if (l.uniqueId) lane.uniqueId = l.uniqueId;
            return lane;
          }),
        };
        if (m.uniqueId) row.uniqueId = m.uniqueId;
        return row;
      });

  const SUPPLIER_OPTIONAL_FIELDS = [
    "phoneNumber",
    "supplierId",
    "contactPerson",
    "address",
    "gstNumber",
    "gstStatus",
    "gstLegalName",
    "gstTradeName",
    "gstConstitutionType",
    "gstTaxPayerType",
    "gstRegistrationDate",
    "gstState",
    "city",
    "state",
    "emailLink",
  ];

  const buildSuppliersPayload = () =>
    suppliers
      .filter((s) => s.supplierName.trim() || s.supplierMailId.trim())
      .map((s) => {
        const row = {
          supplierName: s.supplierName.trim(),
          supplierMailId: s.supplierMailId.trim(),
        };
        SUPPLIER_OPTIONAL_FIELDS.forEach((field) => {
          if (field === "emailLink") return;
          const v = String(s[field] || "").trim();
          if (v) row[field] = v;
        });
        // Like RFQ Activate: always persist portal/email link for Copy in LrfqView.
        // emailSent (not emailLink) controls "already emailed" for Send Email.
        const emailLink =
          String(s.emailLink || "").trim() ||
          buildLrfqSupplierEmailLink(lrfqId, s.supplierMailId, s.supplierId);
        if (emailLink) row.emailLink = emailLink;
        // Persist so edit + Update LRFQ does not forget who was already emailed.
        if (s.emailSent) row.emailSent = true;
        return row;
      });

  const getUserId = () => {
    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/"), 2000);
      return null;
    }
    return userId;
  };

  const buildBody = () => ({
    lrfqNumber: details.lrfqNumber.trim(),
    lrfqDate: formatDateForApi(details.lrfqDate),
    lrfqDueDate: formatDateTimeForApi(details.lrfqDueDate, details.lrfqDueTime),
    lrfqSummary: details.lrfqSummary.trim(),
    lrfqCurrency: details.lrfqCurrency.trim(),
    terms: buildTermsPayload(),
    materials: buildMaterialsPayload(),
    suppliers: buildSuppliersPayload(),
    // requiresPdfUpload: true if any saved file is marked mandatory (backward compat boolean)
    requiresPdfUpload: mandatoryPdfKeys.size > 0,
    // full list of s3Keys that require supplier to upload a signed PDF
    requiresPdfUploadKeys: [...mandatoryPdfKeys],
  });

  const postRequest = async (payload) => {
    const response = await axios.post(API_URL, payload);
    const responseData = parseApiResponse(response.data);
    if (!isApiSuccess(responseData)) {
      toast.error(responseData?.message || "Failed to save Logistics RFQ", {
        autoClose: 2000,
      });
      return { ok: false, responseData };
    }
    return { ok: true, responseData };
  };

  const extractCreatedId = (responseData) => {
    const data = responseData?.data;
    if (!data) return responseData?.id || responseData?._id || null;
    if (typeof data === "string") return data;
    return data._id || data.id || null;
  };

  const persistPendingAttachments = async () => {
    if (pendingFiles.length === 0) return true;
    const userId = getUserId();
    if (!userId) return false;

    const lrfqNumber = details.lrfqNumber.trim();
    if (!lrfqNumber) {
      toast.error("LRFQ number is required for attachments", {
        autoClose: 2000,
      });
      return false;
    }

    setIsUploadingAttachments(true);
    try {
      const files = pendingFiles;
      const contentTypes = files.map((f) => resolveFileContentType(f));

      const response = await axios.post(API_URL, {
        action: "get-lrfq-owner-attachment-upload-presigned-url",
        lrfqNumber: lrfqNumber,
        fileNames: files.map((f) => f.name),
        contentTypes,
        expiresIn: 7200,
      });
      const responseData = parseApiResponse(response.data);

      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        toast.error(responseData?.message || "Failed to get upload URLs", {
          autoClose: 2000,
        });
        return false;
      }
      const presigned = responseData.files;
      if (presigned.length !== files.length) {
        toast.error("Upload URL count mismatch", { autoClose: 2000 });
        return false;
      }

      for (let i = 0; i < files.length; i++) {
        const contentType =
          presigned[i].contentType ||
          contentTypes[i] ||
          "application/octet-stream";
        await putFileToPresignedUrl(
          presigned[i].uploadUrl,
          files[i],
          contentType,
        );
      }

      const newOwner = presigned.map((f, i) => ({
        s3Key: f.s3Key,
        originalFileName: f.originalFileName || f.fileName || files[i].name,
        contentType:
          f.contentType || contentTypes[i] || "application/octet-stream",
      }));

      const { ok, responseData: updateData } = await postRequest({
        action: "update-lrfq",
        id: lrfqId,
        currentUserId: userId,
        updatedBy: userId,
        lrfqOwner: newOwner,
        appendLrfqOwner: ownerAttachments.length > 0,
      });
      if (!ok) return false;

      const updated = updateData?.data;
      if (Array.isArray(updated?.lrfqOwner)) {
        setOwnerAttachments(updated.lrfqOwner.map(mapOwnerFromApi));
      } else {
        setOwnerAttachments((prev) => [
          ...prev,
          ...newOwner.map((o) => ({
            ...o,
            uploadedAt: new Date().toISOString(),
            url: "",
          })),
        ]);
      }
      setPendingFiles([]);
      // Migrate mandatory names → mandatory s3Keys now that we have real keys
      setMandatoryPdfFileNames((prevNames) => {
        if (prevNames.size === 0) return prevNames;
        // newOwner[i].originalFileName corresponds to files[i].name
        const addKeys = newOwner
          .filter((o, i) => prevNames.has(files[i]?.name))
          .map((o) => o.s3Key)
          .filter(Boolean);
        if (addKeys.length === 0) return new Set();
        setMandatoryPdfKeys((prev) => new Set([...prev, ...addKeys]));
        return new Set();
      });
      return true;
    } catch (error) {
      console.error("Upload attachments error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to upload attachments", {
        autoClose: 2000,
      });
      return false;
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const saveTabAttachments = async ({ silent = false } = {}) => {
    if (!lrfqId) return false;
    if (pendingFiles.length === 0) return true;
    if (!silent) setIsLoading(true);
    try {
      const ok = await persistPendingAttachments();
      if (ok && !silent)
        toast.success("Attachments saved", { autoClose: 1500 });
      return ok;
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    if (
      ownerAttachments.length + pendingFiles.length + files.length >
      MAX_OWNER_ATTACHMENTS
    ) {
      toast.error(`Maximum ${MAX_OWNER_ATTACHMENTS} attachments allowed`, {
        autoClose: 2000,
      });
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
  };

  const removePendingAttachment = (index) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        setMandatoryPdfFileNames((keys) => {
          const next = new Set(keys);
          next.delete(removed.name);
          return next;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const downloadOwnerAttachment = async (s3Key) => {
    if (!s3Key) return;
    try {
      const response = await axios.post(API_URL, {
        action: "get-lrfq-owner-attachment-download-presigned-url",
        s3Keys: [s3Key],
        expiresIn: 3600,
      });
      const responseData = parseApiResponse(response.data);
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || "Failed to get download URL", {
          autoClose: 2000,
        });
        return;
      }
      const url =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl;
      if (!url) {
        toast.error("Download URL not available", { autoClose: 2000 });
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Download attachment error:", error);
      toast.error("Failed to download attachment", { autoClose: 2000 });
    }
  };

  const removeOwnerAttachment = async (s3Key) => {
    const userId = getUserId();
    if (!userId || !lrfqId) return;
    setIsUploadingAttachments(true);
    try {
      const remaining = ownerAttachments.filter((a) => a.s3Key !== s3Key);
      const { ok, responseData } = await postRequest({
        action: "update-lrfq",
        id: lrfqId,
        currentUserId: userId,
        updatedBy: userId,
        lrfqOwner: remaining.map((a) => ({
          s3Key: a.s3Key,
          originalFileName: a.originalFileName,
          contentType: a.contentType,
        })),
        appendLrfqOwner: false,
      });
      if (!ok) return;
      setOwnerAttachments(
        Array.isArray(responseData?.data?.lrfqOwner)
          ? responseData.data.lrfqOwner.map(mapOwnerFromApi)
          : remaining,
      );
      // Remove from mandatory keys if it was marked
      setMandatoryPdfKeys((prev) => {
        const next = new Set(prev);
        next.delete(s3Key);
        return next;
      });
      toast.success("Attachment removed", { autoClose: 1500 });
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const getPendingSuppliers = () =>
    suppliers.filter(
      (s) =>
        s.supplierName.trim() &&
        s.supplierMailId.trim() &&
        !supplierAlreadyEmailed(s),
    );
  const getAlreadyEmailedSuppliers = () =>
    suppliers.filter(
      (s) =>
        s.supplierName.trim() &&
        s.supplierMailId.trim() &&
        supplierAlreadyEmailed(s),
    );
  const hasPendingSuppliersToEmail = () => getPendingSuppliers().length > 0;

  const hasCompleteSuppliers = () => {
    const filled = suppliers.filter(
      (s) => s.supplierName.trim() && s.supplierMailId.trim(),
    );
    return (
      filled.length > 0 &&
      filled.every((s) => EMAIL_REGEX.test(s.supplierMailId.trim()))
    );
  };

  const isReadyToSendEmail =
    canAccessPreview &&
    Boolean(pdfLink.trim() || pdfDownloadUrl.trim()) &&
    hasCompleteSuppliers();

  const sendLrfqEmail = async () => {
    if (!lrfqId) {
      toast.error("Save LRFQ details first", { autoClose: 2000 });
      return;
    }
    if (!isActive) {
      toast.error("Activate LRFQ before sending email", { autoClose: 2000 });
      return;
    }
    if (!validateDetails() || !validateMaterials() || !validateSuppliers())
      return;
    if (!pdfLink.trim() && !pdfDownloadUrl.trim()) {
      toast.error(
        "PDF preview is not available yet. Please try again shortly.",
        { autoClose: 2000 },
      );
      return;
    }
    if (!hasPendingSuppliersToEmail()) {
      toast.info("All suppliers have already been emailed.", {
        autoClose: 2500,
      });
      return;
    }
    const userId = getUserId();
    if (!userId) return;

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails);
    const invalidCc = ccEmails.find((e) => !isValidEmailAddress(e));
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 });
      return;
    }
    if (ccEmails.length === 0) {
      toast.error(
        "Could not get your email for CC. Add CC emails or check your profile.",
        { autoClose: 2000 },
      );
      return;
    }

    setIsSendingEmail(true);
    try {
      // Match RFQ: send to pending by emailSent, even if Activate already stored emailLink.
      const toEmails = getPendingSuppliers().map((s) =>
        s.supplierMailId.trim(),
      );
      const response = await axios.post(API_URL, {
        action: "send-lrfq-email",
        id: lrfqId,
        userid: userId,
        toEmails,
        ccEmails,
        onlyNewSuppliers: true,
        sendToAll: false,
      });
      const responseData = parseApiResponse(response.data);
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || "Failed to send LRFQ email", {
          autoClose: 2000,
        });
        return;
      }

      const data = responseData?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      const skipped = Array.isArray(data.skippedAlreadyEmailed)
        ? data.skippedAlreadyEmailed
        : [];
      const total = Number(data.totalRecipients);
      const sentCount = Number.isFinite(total) ? total : results.length;

      if (sentCount === 0) {
        toast.info(
          skipped.length > 0
            ? `Already emailed: ${skipped.join(", ")}`
            : responseData?.message || "No new supplier emails to send",
          { autoClose: 3000 },
        );
        await refreshLrfqById();
        return;
      }

      const byMail = new Map(
        results.map((r) => [
          String(r.to || "")
            .trim()
            .toLowerCase(),
          r,
        ]),
      );
      setSuppliers((prev) =>
        prev.map((s) => {
          const r = byMail.get(s.supplierMailId.trim().toLowerCase());
          if (!r || r.success === false) return s;
          const emailLink =
            String(r.quoteLink || "").trim() ||
            String(s.emailLink || "").trim() ||
            buildLrfqSupplierEmailLink(
              lrfqId,
              s.supplierMailId,
              s.supplierId,
            );
          return { ...s, emailLink, emailSent: true };
        }),
      );
      setIsMailSent(true);
      let msg = responseData?.message || "LRFQ email sent successfully";
      if (skipped.length > 0)
        msg += ` Skipped (already emailed): ${skipped.join(", ")}`;
      toast.success(msg, { autoClose: 3000 });
      await refreshLrfqById();
    } catch (error) {
      console.error("Send LRFQ email error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "An error occurred while sending email", {
        autoClose: 2000,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  /** Creates the draft on first save, updates it afterwards. */
  const saveDraft = async ({ silent = false, extra = {} } = {}) => {
    const userId = getUserId();
    if (!userId) return { ok: false };
    if (!silent) setIsLoading(true);
    try {
      let payload;
      if (lrfqId) {
        payload = {
          action: "update-lrfq",
          id: lrfqId,
          currentUserId: userId,
          updatedBy: userId,
          ...buildBody(),
          ...extra,
        };
      } else {
        payload = {
          action: "create-lrfq",
          currentUserId: userId,
          createdBy: userId,
          ...buildBody(),
          ...extra,
        };
      }
      const result = await postRequest(payload);
      if (!result.ok) return result;

      if (!lrfqId) {
        const newId = extractCreatedId(result.responseData);
        if (newId) {
          setLrfqId(newId);
          navigate(`/logisticsRfqCreate/${newId}`, { replace: true });
        }
      }
      if (!silent) toast.success("Draft saved", { autoClose: 1500 });
      return result;
    } catch (error) {
      console.error("Save Logistics RFQ error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      if (!silent) {
        toast.error(
          errData?.message || "An error occurred while saving Logistics RFQ",
          {
            autoClose: 2000,
          },
        );
      }
      return { ok: false };
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleBack = async (e) => {
    e.preventDefault();
    if (lrfqId) await saveDraft({ silent: true });
    navigate("/logisticsRfq");
  };

  const TAB_ORDER = [
    "details",
    "materials",
    "attachments",
    "supplier",
    "preview",
  ];
  const isForwardMove = (from, to) =>
    TAB_ORDER.indexOf(to) > TAB_ORDER.indexOf(from);

  const goToTab = async (nextTab) => {
    if (nextTab === activeTab) return;
    if (nextTab === "preview" && !canAccessPreview) return;
    const forward = isForwardMove(activeTab, nextTab);

    if (activeTab === "details") {
      if (!validateDetails()) return;
      if (!(await saveDraft({ silent: !forward })).ok) return;
    } else if (activeTab === "materials") {
      if (forward) {
        if (!validateMaterials()) return;
        if (!(await saveDraft()).ok) return;
      } else if (lrfqId) {
        await saveDraft({ silent: true });
      }
    } else if (activeTab === "attachments") {
      await saveTabAttachments({ silent: true });
    } else if (activeTab === "supplier") {
      if (!forward && lrfqId) await saveDraft({ silent: true });
    }

    setActiveTab(nextTab);
  };

  const handleDownloadSample = async () => {
    const userId = getUserId();
    if (!userId) return;
    setIsDownloadingSample(true);
    try {
      const response = await axios.post(API_URL, {
        action: "download-lrfq-material-import-template",
        currentUserId: userId,
      });
      const data = response.data;
      const parsed = parseApiResponse(data);

      const link =
        parsed?.downloadUrl ||
        parsed?.url ||
        parsed?.data?.downloadUrl ||
        parsed?.data?.url;
      if (link) {
        window.open(link, "_blank", "noopener,noreferrer");
        return;
      }

      const base64 = extractExcelBase64(parsed) || extractExcelBase64(data);
      if (!base64) throw new Error("Invalid template data received");

      downloadXlsxFromBase64(
        base64,
        parsed?.fileName ||
          parsed?.data?.fileName ||
          "LRFQ-Materials-Lanes-Sample.xlsx",
      );
    } catch (error) {
      console.error("Download template error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to download sample Excel", {
        autoClose: 2500,
      });
    } finally {
      setIsDownloadingSample(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast.error("Please choose an Excel file", { autoClose: 2000 });
      return;
    }
    const userId = getUserId();
    if (!userId) return;

    setIsImporting(true);
    try {
      const fileBase64 = await fileToBase64(importFile);

      const payload = {
        action: "import-lrfq-materials",
        currentUserId: userId,
        fileBase64,
        fileName: importFile.name,
        sourceCode: importMeta.sourceCode.trim() || "-",
        sourceDescription: importMeta.sourceDescription.trim() || "-",
        hsn: importMeta.hsn.trim() || "-",
        uom: importMeta.uom.trim() || "KGS",
        save: false, // rows fill the table and are saved with Next, like manual entry
      };
      if (lrfqId) payload.id = lrfqId;

      const response = await axios.post(API_URL, payload);
      const responseData = parseApiResponse(response.data);

      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || "Failed to import Excel", {
          autoClose: 3000,
        });
        return;
      }

      const list = Array.isArray(responseData.materials)
        ? responseData.materials
        : [];
      if (list.length === 0) {
        toast.error("No data found in the Excel file", { autoClose: 2500 });
        return;
      }

      // Map off the critical path lightly; truck body via O(1) lookup.
      const imported = list.map((m, i) => {
        const mapped = mapMaterialFromApi(m, i);
        if (!mapped.boq.length) return mapped;
        return {
          ...mapped,
          boq: mapped.boq.map((lane) => ({
            ...lane,
            truckBody:
              TRUCK_BODY_LOOKUP[String(lane.truckBody || "").toLowerCase()] ||
              lane.truckBody,
          })),
        };
      });
      const laneCount =
        Number(responseData.boqCount) ||
        imported.reduce((sum, m) => sum + (m.boq?.length || 0), 0);

      // Unblock Import UI before mounting thousands of table inputs.
      setShowImportModal(false);
      setImportFile(null);
      setImportMeta({
        sourceCode: "",
        sourceDescription: "",
        hsn: "",
        uom: "KGS",
      });
      if (importFileInputRef.current) importFileInputRef.current.value = "";
      setIsImporting(false);
      setExpandedBoqIndexes(new Set());
      setBoqLanePageByMaterial({});
      setMaterialsPage(0);

      const applyImportedRows = () => {
        setMaterials((prev) => {
          const kept = prev.filter((m) => !isMaterialEmpty(m));
          return [...kept, ...imported].map((m, i) => ({
            ...m,
            boq: renumberLanes(m.boq, i + 1),
          }));
        });
        toast.success(
          responseData.message ||
            `Imported ${laneCount || imported.length} lane(s) across ${imported.length} material(s). Expand a row to edit BOQ lanes.`,
          { autoClose: 3500 },
        );
      };

      // Let the modal close / spinner clear, then apply heavy state in a transition.
      window.setTimeout(() => {
        startTransition(applyImportedRows);
      }, 0);
      return;
    } catch (error) {
      console.error("Import Excel error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(errData?.message || "Failed to import Excel", {
        autoClose: 3000,
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateDetails() || !validateMaterials() || !validateSuppliers())
      return;
    if (!lrfqId) {
      toast.error("Save LRFQ details first", { autoClose: 2000 });
      return;
    }

    setIsLoading(true);
    try {
      if (pendingFiles.length > 0 && !(await persistPendingAttachments()))
        return;

      // Persist emailLink via update-lrfq (buildSuppliersPayload) so local works without send-email/AWS.
      const result = await saveDraft({
        silent: true,
        extra: { status: FINAL_STATUS, lrfqStatus: "open" },
      });
      if (!result.ok) return;

      setRecordStatus(FINAL_STATUS);
      setShowPreviewStep(true);

      // Persist portal links for Copy. Keep emailSent so already-emailed suppliers
      // stay skipped when a new contact is added and Update LRFQ is clicked again.
      setSuppliers((prev) =>
        prev.map((s) => {
          const mail = String(s.supplierMailId || "").trim();
          if (!mail) return s;
          const emailLink =
            String(s.emailLink || "").trim() ||
            buildLrfqSupplierEmailLink(lrfqId, mail, s.supplierId);
          return emailLink ? { ...s, emailLink } : s;
        }),
      );

      const data = result.responseData?.data || {};
      if (data.pdfLink) setPdfLink(data.pdfLink);
      const previewUrl = getLrfqPdfPreviewUrl(data);
      if (previewUrl) setPdfDownloadUrl(previewUrl);
      if (!data.pdfLink && !previewUrl) await refreshLrfqById();

      setActiveTab("preview");
      toast.success("LRFQ saved. Review PDF and send email.", {
        autoClose: 2000,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setIsLoading(false);
    }
  };
  const tabClass = (tab) =>
    activeTab === tab ? "nav-link active" : "nav-link";

  const headCellStyle = { fontSize: "12px", whiteSpace: "nowrap" };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">
                {editId ? "Edit Logistics RFQ" : "Create Logistics RFQ"}
              </h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/logisticsRfq" onClick={handleBack}>
                    <i className="fa-solid fa-arrow-left-long" /> Back
                  </Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline card-tabs">
                <div className="card-header p-0 pt-1 border-bottom-0">
                  <ul className="nav nav-tabs" role="tablist">
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass("details")}
                        onClick={() => goToTab("details")}
                      >
                        LRFQ Details
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass("materials")}
                        onClick={() => goToTab("materials")}
                      >
                        Materials &amp; Lines
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass("attachments")}
                        onClick={() => goToTab("attachments")}
                      >
                        Attachments
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass("supplier")}
                        onClick={() => goToTab("supplier")}
                      >
                        Supplier Email
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={tabClass("preview")}
                        onClick={() => canAccessPreview && goToTab("preview")}
                        disabled={!canAccessPreview}
                        style={
                          !canAccessPreview
                            ? { opacity: 0.6, cursor: "not-allowed" }
                            : undefined
                        }
                      >
                        Preview &amp; Email
                      </button>
                    </li>
                  </ul>
                </div>

                <form onSubmit={handleSubmit}>
                  {isLoadingLrfq ? (
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  ) : (
                    <div className="card-body">
                      {activeTab === "details" && (
                        <div className="row">
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>LRFQ Number</label>
                              <div style={{ position: "relative" }}>
                                <input
                                  type="text"
                                  name="lrfqNumber"
                                  className="form-control"
                                  placeholder={
                                    isFetchingNumber
                                      ? "Loading..."
                                      : "LRFQ-ITS-0001"
                                  }
                                  value={details.lrfqNumber}
                                  readOnly
                                  disabled={isFetchingNumber}
                                  style={{
                                    backgroundColor: "#e9ecef",
                                    cursor: "not-allowed",
                                  }}
                                  required
                                />
                                {isFetchingNumber && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      right: "10px",
                                      top: "50%",
                                      transform: "translateY(-50%)",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    <PulseLoader color="#1199B2" size={6} />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>LRFQ Date</label>
                              <input
                                type="date"
                                name="lrfqDate"
                                className="form-control"
                                value={details.lrfqDate}
                                onChange={handleDetailsChange}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>LRFQ Due Date</label>
                              <input
                                type="date"
                                name="lrfqDueDate"
                                className="form-control"
                                value={details.lrfqDueDate}
                                onChange={handleDetailsChange}
                                min={details.lrfqDate || undefined}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Due Time</label>
                              <DueTimePicker
                                value={details.lrfqDueTime}
                                onChange={(t) =>
                                  setDetails((prev) => ({
                                    ...prev,
                                    lrfqDueTime: t,
                                  }))
                                }
                                required={Boolean(details.lrfqDueDate)}
                                placeholder="Select time"
                              />
                            </div>
                          </div>
                          <div className="col-md-2">
                            <div className="form-group">
                              <label>Currency</label>
                              <select
                                name="lrfqCurrency"
                                className="form-control"
                                value={details.lrfqCurrency}
                                onChange={handleDetailsChange}
                              >
                                {CURRENCY_OPTIONS.map((c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="form-group">
                              <label>LRFQ Summary</label>
                              <textarea
                                name="lrfqSummary"
                                className="form-control"
                                rows={4}
                                placeholder="Summary"
                                value={details.lrfqSummary}
                                onChange={handleDetailsChange}
                                required
                              />
                            </div>
                          </div>

                          <div className="col-md-12 mt-3">
                            <label className="mb-2">Terms</label>
                            <div style={{ overflowX: "auto" }}>
                              <table
                                className="table table-bordered table-hover mb-0"
                                style={{ tableLayout: "fixed", width: "100%" }}
                              >
                                <colgroup>
                                  <col style={{ width: "56px" }} />
                                  <col style={{ width: "28%" }} />
                                  <col style={{ width: "calc(72% - 146px)" }} />
                                  <col style={{ width: "90px" }} />
                                </colgroup>
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor: "#003366",
                                      color: "#fff",
                                    }}
                                  >
                                    <th style={headCellStyle}>S.No</th>
                                    <th style={{ fontSize: "12px" }}>Term</th>
                                    <th style={{ fontSize: "12px" }}>
                                      Terms Response
                                    </th>
                                    <th style={{ fontSize: "12px" }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {terms.map((row, index) => (
                                    <tr key={index}>
                                      <td style={{ verticalAlign: "top" }}>
                                        {index + 1}
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.rfqTerm}
                                          onChange={(e) =>
                                            updateTerm(
                                              index,
                                              "rfqTerm",
                                              e.target.value,
                                            )
                                          }
                                        />
                                      </td>
                                      <td style={{ verticalAlign: "top" }}>
                                        <textarea
                                          className="form-control form-control-sm"
                                          rows={2}
                                          value={row.termsResponse}
                                          onChange={(e) =>
                                            updateTerm(
                                              index,
                                              "termsResponse",
                                              e.target.value,
                                            )
                                          }
                                          style={{
                                            width: "100%",
                                            resize: "vertical",
                                            wordBreak: "break-word",
                                          }}
                                        />
                                      </td>
                                      <td
                                        className="text-center"
                                        style={{
                                          whiteSpace: "nowrap",
                                          verticalAlign: "top",
                                        }}
                                      >
                                        <div className="btn-group" role="group">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-success"
                                            onClick={() =>
                                              addTermRowAfter(index)
                                            }
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-plus"
                                              aria-hidden="true"
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() => removeTermRow(index)}
                                            disabled={terms.length <= 1}
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-trash-can"
                                              aria-hidden="true"
                                            />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === "materials" && (
                        <div>
                          <div className="d-flex justify-content-start align-items-center mb-3 flex-wrap">
                            <button
                              type="button"
                              className="btn btn-success mr-2 mb-1"
                              onClick={() => setShowImportModal(true)}
                              disabled={isLoading}
                            >
                              <i className="fa-solid fa-file-import mr-1" />
                              Import Excel
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary mb-1"
                              onClick={handleDownloadSample}
                              disabled={isDownloadingSample}
                            >
                              <i
                                className={`fa-solid ${isDownloadingSample ? "fa-spinner fa-spin" : "fa-download"} mr-1`}
                              />
                              {isDownloadingSample
                                ? "Downloading..."
                                : "Download Sample Excel"}
                            </button>
                            {materials.length > MATERIALS_PAGE_SIZE ? (
                              <span
                                className="text-muted ml-2 mb-1"
                                style={{ fontSize: "12px" }}
                              >
                                {materials.length} materials — showing{" "}
                                {MATERIALS_PAGE_SIZE} per page. Expand BOQ to
                                edit lanes.
                              </span>
                            ) : null}
                          </div>
                          {materials.length > MATERIALS_PAGE_SIZE ? (
                            <div className="d-flex align-items-center mb-2 flex-wrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary mr-2"
                                disabled={materialsPage <= 0}
                                onClick={() =>
                                  setMaterialsPage((p) => Math.max(0, p - 1))
                                }
                              >
                                Prev
                              </button>
                              <span style={{ fontSize: "12px" }} className="mr-2">
                                Page {materialsPage + 1} of{" "}
                                {Math.max(
                                  1,
                                  Math.ceil(
                                    materials.length / MATERIALS_PAGE_SIZE,
                                  ),
                                )}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                disabled={
                                  (materialsPage + 1) * MATERIALS_PAGE_SIZE >=
                                  materials.length
                                }
                                onClick={() =>
                                  setMaterialsPage((p) =>
                                    (p + 1) * MATERIALS_PAGE_SIZE >=
                                    materials.length
                                      ? p
                                      : p + 1,
                                  )
                                }
                              >
                                Next
                              </button>
                            </div>
                          ) : null}
                          <div style={{ overflowX: "auto" }}>
                            <table className="table table-bordered table-hover mb-3">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#003366",
                                    color: "#fff",
                                  }}
                                >
                                  <th style={{ fontSize: "12px" }}>S.No</th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "140px",
                                    }}
                                  >
                                    Cluster
                                  </th>
                                  <th
                                    style={{
                                      fontSize: "12px",
                                      minWidth: "220px",
                                    }}
                                  >
                                    Cluster Description
                                  </th>
                                  <th style={{ fontSize: "12px" }}>HSN</th>
                                  <th style={{ fontSize: "12px" }}>UOM</th>
                                  <th style={{ fontSize: "12px" }}>Quantity</th>
                                  <th
                                    style={{ fontSize: "12px", width: "90px" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {materials
                                  .slice(
                                    materialsPage * MATERIALS_PAGE_SIZE,
                                    materialsPage * MATERIALS_PAGE_SIZE +
                                      MATERIALS_PAGE_SIZE,
                                  )
                                  .flatMap((row, pageOffset) => {
                                  const index =
                                    materialsPage * MATERIALS_PAGE_SIZE +
                                    pageOffset;
                                  const lineNo = index + 1;
                                  const boqExpanded = expandedBoqIndexes.has(
                                    index,
                                  );
                                  const lanePage =
                                    Number(boqLanePageByMaterial[index]) || 0;
                                  const laneStart =
                                    lanePage * BOQ_LANES_PAGE_SIZE;
                                  const visibleLanes = boqExpanded
                                    ? row.boq.slice(
                                        laneStart,
                                        laneStart + BOQ_LANES_PAGE_SIZE,
                                      )
                                    : [];
                                  const lanePageCount = Math.max(
                                    1,
                                    Math.ceil(
                                      row.boq.length / BOQ_LANES_PAGE_SIZE,
                                    ),
                                  );

                                  const parentRow = (
                                    <tr
                                      key={`mat-${index}`}
                                      onClick={() =>
                                        setActiveMaterialRowIndex(index)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        backgroundColor:
                                          activeMaterialRowIndex === index
                                            ? "#e8f4f8"
                                            : undefined,
                                      }}
                                    >
                                      <td style={{ fontWeight: 600 }}>
                                        {lineNo}
                                        {row.boq.length > 0 ? (
                                          <button
                                            type="button"
                                            className="badge badge-warning ml-1 border-0"
                                            style={{
                                              fontSize: "9px",
                                              cursor: "pointer",
                                            }}
                                            title={
                                              boqExpanded
                                                ? "Collapse BOQ lanes"
                                                : "Expand BOQ lanes"
                                            }
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleBoqExpand(index);
                                            }}
                                          >
                                            <i
                                              className={`fa-solid fa-chevron-${boqExpanded ? "up" : "down"} mr-1`}
                                            />
                                            BOQ ({row.boq.length})
                                          </button>
                                        ) : null}
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.sourceCode}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "sourceCode",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="Source code"
                                        />
                                      </td>
                                      <td
                                        style={{
                                          verticalAlign: "top",
                                          minWidth: "220px",
                                        }}
                                      >
                                        <textarea
                                          className="form-control form-control-sm"
                                          rows={2}
                                          value={row.sourceDescription}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "sourceDescription",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="Source description"
                                          style={{
                                            width: "100%",
                                            minWidth: "200px",
                                            resize: "vertical",
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                          }}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.hsn}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "hsn",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      <td>
                                        <select
                                          className="form-control form-control-sm"
                                          value={row.uom || ""}
                                          onChange={(e) =>
                                            updateMaterial(
                                              index,
                                              "uom",
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                          disabled={isLoading || isLoadingUnits}
                                        >
                                          <option value="">
                                            {isLoadingUnits
                                              ? "Loading..."
                                              : "UOM"}
                                          </option>
                                          {row.uom &&
                                            !unitOptions.some(
                                              (o) =>
                                                o.unit.toLowerCase() ===
                                                String(row.uom).toLowerCase(),
                                            ) && (
                                              <option value={row.uom}>
                                                {row.uom}
                                              </option>
                                            )}
                                          {unitOptions.map((o) => (
                                            <option
                                              key={o.id || o.unit}
                                              value={o.unit}
                                            >
                                              {o.unit}
                                            </option>
                                          ))}
                                        </select>
                                      </td>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={row.quantity}
                                          onChange={(e) =>
                                            handleQuantityChange(
                                              index,
                                              e.target.value,
                                            )
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                      <td
                                        className="text-center"
                                        style={{ whiteSpace: "nowrap" }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="btn-group" role="group">
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-success"
                                            onClick={() => {
                                              addMaterialRowAfter(index);
                                              setActiveMaterialRowIndex(
                                                index + 1,
                                              );
                                            }}
                                            title="Add material row"
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-plus"
                                              aria-hidden="true"
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-warning"
                                            onClick={() => {
                                              addLane(index);
                                              setActiveMaterialRowIndex(index);
                                            }}
                                            disabled={isLoading}
                                            title="Add lane (truck line) under this material"
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-list-ol"
                                              aria-hidden="true"
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                              handleMaterialDeleteClick(index)
                                            }
                                            disabled={
                                              isLoading ||
                                              (materials.length <= 1 &&
                                                isMaterialEmpty(row))
                                            }
                                            title={
                                              row.boq.length > 0
                                                ? "Delete material and all its lanes"
                                                : "Delete row"
                                            }
                                            style={{ padding: "3px 8px" }}
                                          >
                                            <i
                                              className="fa-solid fa-trash-can"
                                              aria-hidden="true"
                                            />
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  );

                                  const laneRow =
                                    row.boq.length > 0 && !boqExpanded ? (
                                      <tr
                                        key={`mat-${index}-lanes-summary`}
                                        style={{ backgroundColor: "#fff8e6" }}
                                      >
                                        <td />
                                        <td
                                          colSpan={6}
                                          style={{
                                            padding: "8px",
                                            fontSize: "12px",
                                          }}
                                        >
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-warning"
                                            onClick={() =>
                                              toggleBoqExpand(index)
                                            }
                                          >
                                            <i className="fa-solid fa-list-ol mr-1" />
                                            {row.boq.length} BOQ lane
                                            {row.boq.length === 1 ? "" : "s"}{" "}
                                            hidden — click to expand
                                          </button>
                                        </td>
                                      </tr>
                                    ) : row.boq.length > 0 && boqExpanded ? (
                                      <tr
                                        key={`mat-${index}-lanes`}
                                        style={{ backgroundColor: "#fff8e6" }}
                                      >
                                        <td />
                                        <td
                                          colSpan={6}
                                          style={{ padding: "8px" }}
                                        >
                                          {row.boq.length >
                                          BOQ_LANES_PAGE_SIZE ? (
                                            <div className="d-flex align-items-center mb-2 flex-wrap">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary mr-2"
                                                disabled={lanePage <= 0}
                                                onClick={() =>
                                                  setBoqLanePageByMaterial(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: Math.max(
                                                        0,
                                                        lanePage - 1,
                                                      ),
                                                    }),
                                                  )
                                                }
                                              >
                                                Prev lanes
                                              </button>
                                              <span
                                                style={{ fontSize: "12px" }}
                                                className="mr-2"
                                              >
                                                Lanes {laneStart + 1}–
                                                {Math.min(
                                                  laneStart +
                                                    BOQ_LANES_PAGE_SIZE,
                                                  row.boq.length,
                                                )}{" "}
                                                of {row.boq.length} (page{" "}
                                                {lanePage + 1}/{lanePageCount})
                                              </span>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary mr-2"
                                                disabled={
                                                  laneStart +
                                                    BOQ_LANES_PAGE_SIZE >=
                                                  row.boq.length
                                                }
                                                onClick={() =>
                                                  setBoqLanePageByMaterial(
                                                    (prev) => ({
                                                      ...prev,
                                                      [index]: lanePage + 1,
                                                    }),
                                                  )
                                                }
                                              >
                                                Next lanes
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-link"
                                                onClick={() =>
                                                  toggleBoqExpand(index)
                                                }
                                              >
                                                Collapse
                                              </button>
                                            </div>
                                          ) : (
                                            <div className="mb-2">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-link p-0"
                                                onClick={() =>
                                                  toggleBoqExpand(index)
                                                }
                                              >
                                                Collapse lanes
                                              </button>
                                            </div>
                                          )}
                                          <div style={{ overflowX: "auto" }}>
                                            <table className="table table-sm table-bordered mb-0">
                                              <thead>
                                                <tr
                                                  style={{
                                                    backgroundColor: "#856404",
                                                    color: "#fff",
                                                  }}
                                                >
                                                  <th style={headCellStyle}>
                                                    S.No
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Source Pincode
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Destination Pincode
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Source City
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Destination City
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Truck Name
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Truck Body
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Loadability (MT)
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Length (Ft)
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Width (Ft)
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Height (Ft)
                                                  </th>
                                                  <th style={headCellStyle}>
                                                    Quantity
                                                  </th>
                                                  <th
                                                    style={{
                                                      fontSize: "12px",
                                                      width: "60px",
                                                    }}
                                                  >
                                                    Action
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {visibleLanes.map((lane, visibleOffset) => {
                                                  const lIndex =
                                                    laneStart + visibleOffset;
                                                  return (
                                                  <tr
                                                    key={`lane-${index}-${lane.uniqueId || lIndex}`}
                                                  >
                                                    <td
                                                      style={{
                                                        fontWeight: 600,
                                                        color: "#856404",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      {lane.sNo}
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "110px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={
                                                          lane.sourcePincode
                                                        }
                                                        onChange={(e) =>
                                                          handleLanePincode(
                                                            index,
                                                            lIndex,
                                                            "sourcePincode",
                                                            e.target.value,
                                                          )
                                                        }
                                                        inputMode="numeric"
                                                        maxLength={6}
                                                      />
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "110px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={
                                                          lane.destinationPincode
                                                        }
                                                        onChange={(e) =>
                                                          handleLanePincode(
                                                            index,
                                                            lIndex,
                                                            "destinationPincode",
                                                            e.target.value,
                                                          )
                                                        }
                                                        inputMode="numeric"
                                                        maxLength={6}
                                                      />
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "120px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={lane.sourceCity}
                                                        onChange={(e) =>
                                                          updateLane(
                                                            index,
                                                            lIndex,
                                                            "sourceCity",
                                                            e.target.value,
                                                          )
                                                        }
                                                      />
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "120px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={
                                                          lane.destinationCity
                                                        }
                                                        onChange={(e) =>
                                                          updateLane(
                                                            index,
                                                            lIndex,
                                                            "destinationCity",
                                                            e.target.value,
                                                          )
                                                        }
                                                      />
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "120px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={lane.truckName}
                                                        onChange={(e) =>
                                                          updateLane(
                                                            index,
                                                            lIndex,
                                                            "truckName",
                                                            e.target.value,
                                                          )
                                                        }
                                                      />
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "110px",
                                                      }}
                                                    >
                                                      <select
                                                        className="form-control form-control-sm"
                                                        value={lane.truckBody}
                                                        onChange={(e) =>
                                                          updateLane(
                                                            index,
                                                            lIndex,
                                                            "truckBody",
                                                            e.target.value,
                                                          )
                                                        }
                                                      >
                                                        <option value="">
                                                          Select
                                                        </option>
                                                        {TRUCK_BODY_OPTIONS.map(
                                                          (b) => (
                                                            <option
                                                              key={b}
                                                              value={b}
                                                            >
                                                              {b}
                                                            </option>
                                                          ),
                                                        )}
                                                      </select>
                                                    </td>
                                                    <td
                                                      style={{
                                                        minWidth: "120px",
                                                      }}
                                                    >
                                                      <input
                                                        type="text"
                                                        className="form-control form-control-sm"
                                                        value={
                                                          lane.truckLoadabilityMt
                                                        }
                                                        onChange={(e) =>
                                                          updateLane(
                                                            index,
                                                            lIndex,
                                                            "truckLoadabilityMt",
                                                            e.target.value,
                                                          )
                                                        }
                                                        placeholder="e.g. 9 MT-FTL"
                                                      />
                                                    </td>
                                                    {[
                                                      "truckLengthFt",
                                                      "truckWidthFt",
                                                      "truckHeightFt",
                                                    ].map((field) => (
                                                      <td
                                                        key={field}
                                                        style={{
                                                          minWidth: "90px",
                                                        }}
                                                      >
                                                        <input
                                                          type="text"
                                                          className="form-control form-control-sm"
                                                          value={lane[field]}
                                                          onChange={(e) =>
                                                            handleLaneNumber(
                                                              index,
                                                              lIndex,
                                                              field,
                                                              e.target.value,
                                                            )
                                                          }
                                                          inputMode="decimal"
                                                        />
                                                      </td>
                                                    ))}
                                                    <td style={{ minWidth: '90px' }}>
  <input
    type="text"
    className="form-control form-control-sm"
    value={lane.quantity}
    onChange={(e) => handleLaneNumber(index, lIndex, 'quantity', e.target.value)}
    inputMode="decimal"
    placeholder="Qty"
  />
</td>
                                                    <td
                                                      className="text-center"
                                                      style={{
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      <button
                                                        type="button"
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() =>
                                                          removeLane(
                                                            index,
                                                            lIndex,
                                                          )
                                                        }
                                                        disabled={isLoading}
                                                        title="Remove lane"
                                                        style={{
                                                          padding: "3px 8px",
                                                        }}
                                                      >
                                                        <i
                                                          className="fa-solid fa-trash-can"
                                                          aria-hidden="true"
                                                        />
                                                      </button>
                                                    </td>
                                                  </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : null;

                                  return laneRow
                                    ? [parentRow, laneRow]
                                    : [parentRow];
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* ============ ATTACHMENTS ============ */}
                      {activeTab === "attachments" && (
                        <div>
                          <div className="row mb-3">
                            <div className="col-md-8">
                              <p
                                className="text-muted mb-2"
                                style={{ fontSize: "13px" }}
                              >
                                Files are stored securely and can be downloaded
                                later.
                              </p>
                              <input
                                ref={attachmentInputRef}
                                type="file"
                                multiple
                                style={{ display: "none" }}
                                onChange={handleAttachmentFileSelect}
                                disabled={isLoading || isUploadingAttachments}
                              />
                              <button
                                type="button"
                                className="btn btn-blue"
                                onClick={() =>
                                  attachmentInputRef.current?.click()
                                }
                                disabled={
                                  isLoading ||
                                  isUploadingAttachments ||
                                  ownerAttachments.length +
                                    pendingFiles.length >=
                                    MAX_OWNER_ATTACHMENTS
                                }
                              >
                                <i className="fa-solid fa-paperclip mr-1" />
                                Choose Files
                              </button>
                              <span
                                className="text-muted ml-2"
                                style={{ fontSize: "12px" }}
                              >
                                {ownerAttachments.length + pendingFiles.length}{" "}
                                / {MAX_OWNER_ATTACHMENTS} files
                              </span>
                            </div>
                          </div>

                          {isUploadingAttachments && (
                            <div
                              className="mb-3 text-muted"
                              style={{ fontSize: "13px" }}
                            >
                              <span className="mr-2">
                                <PulseLoader color="#1199B2" size={6} />
                              </span>
                              Uploading attachments...
                            </div>
                          )}

                          {/* ── File list with per-file independent Mandatory checkbox ── */}
                          {(ownerAttachments.length > 0 || pendingFiles.length > 0) ? (
                            <div>
                              {ownerAttachments.length > 0 && (
                                <div className="mb-4">
                                  <div
                                    className="fwt_600 mb-2"
                                    style={{ fontSize: "14px", color: "#003366" }}
                                  >
                                    Uploaded attachments
                                  </div>
                                  {/* Column header for the Mandatory checkbox */}
                                  <div
                                    className="d-flex align-items-center mb-1 px-2"
                                    style={{ gap: "15px" }}
                                  >
                                    <div
                                      style={{
                                        width: "60px",
                                        flexShrink: 0,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#003366",
                                        textAlign: "center",
                                      }}
                                    >
                                      Mandatory
                                    </div>
                                    <div style={{ flex: 1 }} />
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "8px",
                                    }}
                                  >
                                    {ownerAttachments.map((att) => {
                                      const isChecked = mandatoryPdfKeys.has(att.s3Key);
                                      return (
                                        <div
                                          key={att.s3Key}
                                          className="d-flex align-items-center p-2"
                                          style={{
                                            border: isChecked
                                              ? "1px solid #1199B2"
                                              : "1px solid #dee2e6",
                                            borderRadius: "6px",
                                            background: isChecked
                                              ? "#f0fafc"
                                              : "#f8f9fa",
                                            gap: "12px",
                                          }}
                                        >
                                          {/* Independent checkbox per file */}
                                          <div
                                            style={{
                                              width: "60px",
                                              flexShrink: 0,
                                              display: "flex",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                setMandatoryPdfKeys((prev) => {
                                                  const next = new Set(prev);
                                                  if (e.target.checked) {
                                                    next.add(att.s3Key);
                                                  } else {
                                                    next.delete(att.s3Key);
                                                  }
                                                  return next;
                                                });
                                              }}
                                              disabled={isActive || isUploadingAttachments}
                                              title="Check to require supplier to upload a signed version of this PDF"
                                              style={{
                                                width: "15px",
                                                height: "15px",
                                                cursor: isActive ? "not-allowed" : "pointer",
                                                accentColor: "#1199B2",
                                              }}
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                              style={{
                                                fontSize: "13px",
                                                fontWeight: 600,
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              <i className="fa-solid fa-file-pdf mr-1 text-danger" />
                                              {att.originalFileName || att.s3Key}
                                            </div>
                                            {att.uploadedAt && (
                                              <div
                                                className="text-muted"
                                                style={{ fontSize: "11px" }}
                                              >
                                                Uploaded{" "}
                                                {new Date(att.uploadedAt).toLocaleString()}
                                              </div>
                                            )}
                                          </div>
                                          <div
                                            className="d-flex"
                                            style={{ gap: "8px", flexShrink: 0 }}
                                          >
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-primary"
                                              onClick={() => downloadOwnerAttachment(att.s3Key)}
                                              disabled={isUploadingAttachments}
                                            >
                                              <i className="fa-solid fa-download mr-1" />
                                              Download
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger"
                                              onClick={() => removeOwnerAttachment(att.s3Key)}
                                              disabled={isUploadingAttachments || isActive}
                                            >
                                              <i className="fa-solid fa-trash-can" />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {mandatoryPdfKeys.size > 0 && (
                                    <div
                                      className="mt-2"
                                      style={{
                                        fontSize: "12px",
                                        color: "#0c5460",
                                        background: "#d1ecf1",
                                        border: "1px solid #bee5eb",
                                        borderRadius: "4px",
                                        padding: "6px 10px",
                                      }}
                                    >
                                      <i className="fa-solid fa-circle-info mr-1" />
                                      The supplier must upload a signed PDF for the checked file(s) when updating their price.
                                    </div>
                                  )}
                                </div>
                              )}

                              {pendingFiles.length > 0 && (
                                <div>
                                  <div
                                    className="fwt_600 mb-2"
                                    style={{ fontSize: "14px", color: "#003366" }}
                                  >
                                    Pending upload (saved when you click Next)
                                  </div>
                                  {/* Column header for the Mandatory checkbox */}
                                  <div
                                    className="d-flex align-items-center mb-1 px-2"
                                    style={{ gap: "12px" }}
                                  >
                                    <div
                                      style={{
                                        width: "60px",
                                        flexShrink: 0,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#003366",
                                        textAlign: "center",
                                      }}
                                    >
                                      Mandatory
                                    </div>
                                    <div style={{ flex: 1 }} />
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "8px",
                                    }}
                                  >
                                    {pendingFiles.map((file, idx) => {
                                      const isChecked = mandatoryPdfFileNames.has(file.name);
                                      return (
                                        <div
                                          key={`${file.name}-${idx}`}
                                          className="d-flex align-items-center p-2"
                                          style={{
                                            border: isChecked
                                              ? "1px dashed #1199B2"
                                              : "1px dashed #cbd5e1",
                                            borderRadius: "6px",
                                            background: isChecked ? "#f0fafc" : "#fbfdff",
                                            gap: "12px",
                                          }}
                                        >
                                          {/* Independent checkbox per pending file */}
                                          <div
                                            style={{
                                              width: "54px",
                                              flexShrink: 0,
                                              display: "flex",
                                              justifyContent: "center",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => {
                                                setMandatoryPdfFileNames((prev) => {
                                                  const next = new Set(prev);
                                                  if (e.target.checked) {
                                                    next.add(file.name);
                                                  } else {
                                                    next.delete(file.name);
                                                  }
                                                  return next;
                                                });
                                              }}
                                              disabled={isUploadingAttachments}
                                              title="Check to require supplier to upload a signed version of this PDF"
                                              style={{
                                                width: "15px",
                                                height: "15px",
                                                cursor: "pointer",
                                                accentColor: "#1199B2",
                                              }}
                                            />
                                          </div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                              style={{
                                                fontSize: "13px",
                                                wordBreak: "break-word",
                                              }}
                                            >
                                              {file.name}
                                            </div>
                                            <div
                                              className="text-muted"
                                              style={{ fontSize: "11px" }}
                                            >
                                              {formatFileSize(file.size)}
                                              {file.type ? ` · ${file.type}` : ""}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => removePendingAttachment(idx)}
                                            disabled={isUploadingAttachments}
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              className="text-center text-muted py-4"
                              style={{
                                border: "1px dashed #dee2e6",
                                borderRadius: "6px",
                                fontSize: "13px",
                              }}
                            >
                              <i
                                className="fa-solid fa-cloud-arrow-up mb-2"
                                style={{ fontSize: "24px", display: "block" }}
                              />
                              No attachments yet. Click &quot;Choose
                              Files&quot; to add documents.
                            </div>
                          )}
                        </div>
                      )}

                      {/* ============ SUPPLIERS ============ */}
                      {activeTab === "supplier" && (
                        <div>
                          <div className="row align-items-end mb-3">
                            <div className="col-md-6">
                              <div className="form-group mb-0">
                                <label>Search Supplier</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by supplier name or email..."
                                    value={supplierSearch}
                                    onChange={handleSupplierSearchChange}
                                    onKeyPress={handleSupplierSearchKeyPress}
                                  />
                                  <div className="input-group-append">
                                    <button
                                      type="button"
                                      className="btn btn-blue"
                                      onClick={handleSupplierSearch}
                                      disabled={supplierSearchLoading}
                                    >
                                      {supplierSearchLoading ? (
                                        <PulseLoader color="#fff" size={6} />
                                      ) : (
                                        <i className="fa-solid fa-search" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {supplierSearchResults.length > 0 && (
                            <div
                              className="mb-3"
                              style={{
                                border: "1px solid #dee2e6",
                                borderRadius: "6px",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#f8f9fa",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                }}
                              >
                                Supplier search results (
                                {supplierSearchResults.length})
                              </div>
                              <div
                                style={{
                                  maxHeight: "360px",
                                  overflowY: "auto",
                                }}
                              >
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#003366",
                                        color: "#fff",
                                      }}
                                    >
                                      <th style={{ fontSize: "11px" }}>
                                        Client Name
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Email
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Contact Person
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Phone
                                      </th>
                                      <th
                                        style={{
                                          fontSize: "11px",
                                          width: "70px",
                                        }}
                                      />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {supplierSearchResults.flatMap(
                                      (supplier, sIndex) => {
                                        const contacts =
                                          supplier.contacts &&
                                          supplier.contacts.length
                                            ? supplier.contacts
                                            : [
                                                {
                                                  contactPerson:
                                                    supplier.contactPerson,
                                                  email: supplier.email,
                                                  phonenumber:
                                                    supplier.phonenumber,
                                                },
                                              ];
                                        return contacts.map(
                                          (contact, cIndex) => {
                                            const supplierContact = {
                                              ...supplier,
                                              contactPerson:
                                                contact.contactPerson || "",
                                              email: contact.email || "",
                                              phonenumber:
                                                contact.phonenumber || "",
                                            };
                                            const alreadyAdded =
                                              isSupplierAlreadyAdded(
                                                contact.email,
                                              );
                                            return (
                                              <tr
                                                key={`${supplier._id || supplier.id || sIndex}-${cIndex}`}
                                                style={
                                                  alreadyAdded
                                                    ? {
                                                        backgroundColor:
                                                          "#f0f0f0",
                                                      }
                                                    : undefined
                                                }
                                              >
                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {supplier.clientname || "-"}
                                                </td>
                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.email || "-"}
                                                </td>
                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.contactPerson || "-"}
                                                </td>
                                                <td
                                                  style={{ fontSize: "12px" }}
                                                >
                                                  {contact.phonenumber || "-"}
                                                </td>
                                                <td className="text-center">
                                                  <button
                                                    type="button"
                                                    className={`btn btn-sm ${alreadyAdded ? "btn-secondary" : "btn-blue"}`}
                                                    onClick={() =>
                                                      selectSupplier(
                                                        supplierContact,
                                                      )
                                                    }
                                                    disabled={
                                                      !contact.email?.trim() ||
                                                      alreadyAdded
                                                    }
                                                  >
                                                    {alreadyAdded
                                                      ? "Added"
                                                      : "Select"}
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          },
                                        );
                                      },
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          <div style={{ overflowX: "auto" }}>
                            <table className="table table-bordered table-hover mb-0">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: "#003366",
                                    color: "#fff",
                                  }}
                                >
                                  <th style={{ fontSize: "12px" }}>S.No</th>
                                  <th style={{ fontSize: "12px" }}>
                                    Supplier Name
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Supplier Mail ID
                                  </th>
                                  <th style={{ fontSize: "12px" }}>
                                    Phone Number
                                  </th>
                                  <th
                                    style={{ fontSize: "12px", width: "90px" }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {suppliers.map((row, index) => (
                                  <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.supplierName}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "supplierName",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="email"
                                        className="form-control form-control-sm"
                                        value={row.supplierMailId}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "supplierMailId",
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        value={row.phoneNumber || ""}
                                        onChange={(e) =>
                                          updateSupplier(
                                            index,
                                            "phoneNumber",
                                            normalizePhoneNumber(
                                              e.target.value,
                                            ),
                                          )
                                        }
                                        onPaste={(e) => {
                                          e.preventDefault();
                                          updateSupplier(
                                            index,
                                            "phoneNumber",
                                            normalizePhoneNumber(
                                              e.clipboardData.getData("text"),
                                            ),
                                          );
                                        }}
                                        maxLength={10}
                                        inputMode="numeric"
                                      />
                                    </td>
                                    <td
                                      className="text-center"
                                      style={{ whiteSpace: "nowrap" }}
                                    >
                                      <div className="btn-group" role="group">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-success"
                                          onClick={() =>
                                            addSupplierRowAfter(index)
                                          }
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-plus"
                                            aria-hidden="true"
                                          />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            removeSupplierRow(index)
                                          }
                                          disabled={suppliers.length <= 1}
                                          style={{ padding: "3px 8px" }}
                                        >
                                          <i
                                            className="fa-solid fa-trash-can"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {activeTab === "preview" && canAccessPreview && (
                        <div>
                          <div
                            className="mb-4"
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 14px",
                                backgroundColor: "#f8f9fa",
                                fontWeight: 600,
                                fontSize: "14px",
                              }}
                            >
                              <i className="fa-solid fa-clipboard-check mr-2 text-primary" />
                              Review LRFQ Data
                            </div>
                            <div className="p-3">
                              <div className="row mb-2">
                                <div className="col-md-3">
                                  <strong>LRFQ Number:</strong>{" "}
                                  {details.lrfqNumber || "-"}
                                </div>
                                <div className="col-md-3">
                                  <strong>LRFQ Date:</strong>{" "}
                                  {formatDateForApi(details.lrfqDate) || "-"}
                                </div>
                                <div className="col-md-3">
                                  <strong>Due Date &amp; Time:</strong>{" "}
                                  {formatDateTimeForApi(
                                    details.lrfqDueDate,
                                    details.lrfqDueTime,
                                  ) || "-"}
                                </div>
                                <div className="col-md-3">
                                  <strong>Currency:</strong>{" "}
                                  {details.lrfqCurrency || "-"}
                                </div>
                              </div>
                              <div className="mb-3">
                                <strong>Summary:</strong>{" "}
                                {details.lrfqSummary || "-"}
                              </div>

                              <div className="table-responsive mb-3">
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#003366",
                                        color: "#fff",
                                      }}
                                    >
                                      <th
                                        style={{
                                          fontSize: "11px",
                                          width: "60px",
                                        }}
                                      >
                                        S.No
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Cluster
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Cluster Description
                                      </th>
                                      <th style={{ fontSize: "11px" }}>HSN</th>
                                      <th style={{ fontSize: "11px" }}>UOM</th>
                                      <th style={{ fontSize: "11px" }}>Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {materials
                                      .filter((m) => !isMaterialEmpty(m))
                                      .flatMap((row, index) => {
                                        const lineNo = index + 1;
                                        const lanes = row.boq.filter(
                                          (l) => !isLaneEmpty(l),
                                        );

                                        const parent = (
                                          <tr key={`pv-mat-${index}`}>
                                            <td style={{ fontWeight: 600 }}>
                                              {lineNo}
                                              {lanes.length > 0 && (
                                                <span
                                                  className="badge badge-warning ml-1"
                                                  style={{ fontSize: "9px" }}
                                                >
                                                  BOQ
                                                </span>
                                              )}
                                            </td>
                                            <td>{row.sourceCode || "-"}</td>
                                            <td
                                              style={{
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                maxWidth: "280px",
                                              }}
                                            >
                                              {row.sourceDescription || "-"}
                                            </td>
                                            <td>{row.hsn || "-"}</td>
                                            <td>{row.uom || "-"}</td>
                                            <td>{row.quantity || "-"}</td>
                                          </tr>
                                        );

                                        // Keep preview light: large BOQ grids stay on Materials tab.
                                        const laneRow =
                                          lanes.length > 0 ? (
                                            <tr
                                              key={`pv-mat-${index}-lanes`}
                                              style={{
                                                backgroundColor: "#fff8e6",
                                              }}
                                            >
                                              <td />
                                              <td
                                                colSpan={5}
                                                style={{
                                                  padding: "8px",
                                                  fontSize: "12px",
                                                  color: "#856404",
                                                }}
                                              >
                                                {lanes.length} BOQ lane
                                                {lanes.length === 1
                                                  ? ""
                                                  : "s"}{" "}
                                                — expand on Materials & Lines to
                                                review details
                                              </td>
                                            </tr>
                                          ) : null;

                                        return laneRow
                                          ? [parent, laneRow]
                                          : [parent];
                                      })}
                                  </tbody>
                                </table>
                              </div>

                              {ownerAttachments.length > 0 && (
                                <div className="mb-3">
                                  <strong>Attachments:</strong>
                                  <ul
                                    className="mb-0 mt-1"
                                    style={{ fontSize: "13px" }}
                                  >
                                    {ownerAttachments.map((att) => (
                                      <li key={att.s3Key}>
                                        <button
                                          type="button"
                                          className="btn btn-link btn-sm p-0 align-baseline"
                                          onClick={() =>
                                            downloadOwnerAttachment(att.s3Key)
                                          }
                                        >
                                          {att.originalFileName || att.s3Key}
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0">
                                  <thead>
                                    <tr
                                      style={{
                                        backgroundColor: "#003366",
                                        color: "#fff",
                                      }}
                                    >
                                      <th style={{ fontSize: "11px" }}>
                                        Supplier Name
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Supplier Mail ID
                                      </th>
                                      <th style={{ fontSize: "11px" }}>
                                        Phone Number
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {buildSuppliersPayload().map(
                                      (row, index) => (
                                        <tr key={index}>
                                          <td>{row.supplierName || "-"}</td>
                                          <td>{row.supplierMailId || "-"}</td>
                                          <td>{row.phoneNumber || "-"}</td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          {/* PDF */}
                          <div
                            className="mb-4"
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 14px",
                                backgroundColor: "#f8f9fa",
                                fontWeight: 600,
                                fontSize: "14px",
                              }}
                            >
                              <i className="fa-solid fa-file-pdf mr-2 text-primary" />
                              Preview PDF
                            </div>
                            <div className="p-3">
                              {pdfDownloadUrl ? (
                                <>
                                  <div className="mb-3">
                                    <a
                                      href={pdfDownloadUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="btn btn-sm btn-outline-primary"
                                    >
                                      <i className="fa-solid fa-up-right-from-square mr-1" />
                                      Open PDF in new tab
                                    </a>
                                  </div>
                                  <div
                                    style={{
                                      border: "1px solid #dee2e6",
                                      borderRadius: "4px",
                                      overflow: "hidden",
                                      minHeight: "420px",
                                    }}
                                  >
                                    <iframe
                                      title="LRFQ PDF Preview"
                                      src={pdfDownloadUrl}
                                      style={{
                                        width: "100%",
                                        height: "420px",
                                        border: "none",
                                      }}
                                    />
                                  </div>
                                </>
                              ) : pdfLink ? (
                                <p className="text-muted mb-0">
                                  PDF is stored but a download URL is not
                                  available. Refresh the page or click Update
                                  LRFQ again to get a preview link.
                                </p>
                              ) : (
                                <p className="text-muted mb-0">
                                  PDF is being generated. Go back to the
                                  Supplier Email tab and click Update LRFQ
                                  again.
                                </p>
                              )}
                            </div>
                          </div>

                          <div
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                padding: "10px 14px",
                                backgroundColor: "#f8f9fa",
                                fontWeight: 600,
                                fontSize: "14px",
                              }}
                            >
                              <i className="fa-solid fa-envelope mr-2 text-primary" />
                              Send LRFQ Email
                            </div>
                            <div className="p-3">
                              <div className="form-group mb-2">
                                <label>To Emails (new suppliers)</label>
                                <div
                                  className="txtdsply"
                                  style={{ fontSize: "13px" }}
                                >
                                  {getPendingSuppliers()
                                    .map((s) => s.supplierMailId.trim())
                                    .join(", ") || "—"}
                                </div>
                              </div>

                              <div className="form-group mb-2">
                                <label>CC Emails (your email)</label>
                                <div
                                  className={`txtdsply ${userEmail ? "" : "text-muted"}`}
                                  style={{ fontSize: "13px" }}
                                >
                                  {userEmail ||
                                    "Profile email not available — add CC addresses below"}
                                </div>
                              </div>

                              <div
                                className="form-group mb-2"
                                style={{ position: "relative" }}
                              >
                                <label>Additional CC Emails</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="manager@example.com"
                                  value={additionalCcEmails}
                                  onChange={handleAdditionalCcChange}
                                  onBlur={handleAdditionalCcBlur}
                                  disabled={isSendingEmail}
                                  autoComplete="off"
                                />
                                {(isSearchingCcUsers ||
                                  ccUserSuggestions.length > 0) && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      right: 0,
                                      zIndex: 1060,
                                      backgroundColor: "#fff",
                                      border: "1px solid #d8e0ea",
                                      borderRadius: "8px",
                                      boxShadow:
                                        "0 10px 24px rgba(15, 23, 42, 0.14)",
                                      maxHeight: "210px",
                                      overflowY: "auto",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {isSearchingCcUsers ? (
                                      <div
                                        className="text-muted"
                                        style={{
                                          padding: "10px 12px",
                                          fontSize: "13px",
                                        }}
                                      >
                                        <i className="fa-solid fa-spinner fa-spin mr-2" />
                                        Searching users...
                                      </div>
                                    ) : (
                                      (() => {
                                        const existing = new Set(
                                          buildCcEmails(
                                            userEmail,
                                            additionalCcEmails,
                                          ).map((i) => i.toLowerCase()),
                                        );
                                        const visible =
                                          ccUserSuggestions.filter(
                                            (u) =>
                                              !existing.has(
                                                String(u.email)
                                                  .trim()
                                                  .toLowerCase(),
                                              ),
                                          );
                                        if (visible.length === 0) {
                                          return (
                                            <div
                                              className="text-muted"
                                              style={{
                                                padding: "10px 12px",
                                                fontSize: "13px",
                                              }}
                                            >
                                              No matching users found
                                            </div>
                                          );
                                        }
                                        return visible.map((user) => (
                                          <button
                                            key={user.id || user.email}
                                            type="button"
                                            className="dropdown-item"
                                            style={{
                                              display: "block",
                                              width: "100%",
                                              textAlign: "left",
                                              padding: "8px 12px",
                                              whiteSpace: "normal",
                                              cursor: "pointer",
                                            }}
                                            onMouseDown={(e) => {
                                              e.preventDefault();
                                              handleSelectCcUser(user.email);
                                            }}
                                          >
                                            <span
                                              className="d-block fwt_600"
                                              style={{ fontSize: "13px" }}
                                            >
                                              {user.name || user.email}
                                            </span>
                                            <small className="text-muted">
                                              {user.email}
                                            </small>
                                          </button>
                                        ));
                                      })()
                                    )}
                                  </div>
                                )}
                                {buildCcEmails(userEmail, additionalCcEmails)
                                  .length > 0 && (
                                  <small className="form-text text-muted d-block">
                                    Will CC:{" "}
                                    {buildCcEmails(
                                      userEmail,
                                      additionalCcEmails,
                                    ).join(", ")}
                                  </small>
                                )}
                              </div>

                              <div className="form-group mb-3">
                                <label>Already emailed (skipped)</label>
                                <div
                                  className="txtdsply text-muted"
                                  style={{ fontSize: "13px" }}
                                >
                                  {getAlreadyEmailedSuppliers()
                                    .map((s) => s.supplierMailId.trim())
                                    .join(", ") || "—"}
                                </div>
                              </div>

                              {!isReadyToSendEmail && (
                                <div
                                  className="alert alert-warning py-2 mb-3"
                                  role="alert"
                                >
                                  Complete all LRFQ details, materials,
                                  suppliers, wait for the PDF, and activate the
                                  LRFQ before sending email.
                                </div>
                              )}
                              {isReadyToSendEmail &&
                                !hasPendingSuppliersToEmail() && (
                                  <div
                                    className="alert alert-success py-2 mb-3"
                                    role="alert"
                                  >
                                    <i className="fa-solid fa-circle-check mr-1" />
                                    All suppliers have already been emailed.
                                  </div>
                                )}
                              {isReadyToSendEmail &&
                                hasPendingSuppliersToEmail() &&
                                isMailSent && (
                                  <div
                                    className="alert alert-info py-2 mb-3"
                                    role="alert"
                                  >
                                    <i className="fa-solid fa-circle-info mr-1" />
                                    New supplier(s) added — click Send Email to
                                    notify only them.
                                  </div>
                                )}

                              <button
                                type="button"
                                className="btn btn-blue"
                                onClick={sendLrfqEmail}
                                disabled={
                                  isSendingEmail ||
                                  !isReadyToSendEmail ||
                                  !hasPendingSuppliersToEmail()
                                }
                              >
                                {isSendingEmail ? "Sending..." : "Send Email"}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ============ FOOTER ============ */}
                  <div className="card-footer d-flex justify-content-center align-items-center">
                    {activeTab === "details" && (
                      <button
                        type="button"
                        className="btn btn-blue"
                        onClick={() => goToTab("materials")}
                        disabled={isLoading || isLoadingLrfq}
                      >
                        {isLoading ? "Saving..." : "Next"}
                      </button>
                    )}

                    {activeTab === "materials" && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab("details")}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab("attachments")}
                          disabled={isLoading}
                        >
                          {isLoading ? "Saving..." : "Next"}
                        </button>
                      </>
                    )}

                    {activeTab === "attachments" && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab("materials")}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-blue"
                          onClick={() => goToTab("supplier")}
                          disabled={isLoading || isUploadingAttachments}
                        >
                          {isUploadingAttachments ? "Uploading..." : "Next"}
                        </button>
                      </>
                    )}

                    {activeTab === "supplier" && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab("attachments")}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <button
                          type="submit"
                          className="btn btn-blue"
                          disabled={
                            isLoading ||
                            isLoadingLrfq ||
                            isFetchingNumber ||
                            !details.lrfqNumber
                          }
                        >
                          {isLoading
                            ? "Submitting..."
                            : isActive
                              ? "Update LRFQ"
                              : "Activate LRFQ"}
                        </button>
                      </>
                    )}

                    {activeTab === "preview" && (
                      <>
                        <button
                          type="button"
                          className="btn btn-blue mr-2"
                          onClick={() => goToTab("supplier")}
                          disabled={isLoading}
                        >
                          Previous
                        </button>
                        <Link to="/logisticsRfq" className="btn btn-secondary">
                          Back to List
                        </Link>
                      </>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modalDeleteLrfqMaterial"
        tabIndex={-1}
        aria-labelledby="modalDeleteLrfqMaterialLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalDeleteLrfqMaterialLabel">
                Delete Material
              </h5>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to delete{" "}
                <span className="fwt_600">
                  {materialDeleteIndex != null
                    ? getMaterialLabel(materials[materialDeleteIndex])
                    : "-"}
                </span>
                ?
                {materialDeleteIndex != null &&
                  materials[materialDeleteIndex]?.boq?.length > 0 && (
                    <>
                      <br />
                      <span className="text-muted" style={{ fontSize: "13px" }}>
                        All lanes under this material will also be removed.
                      </span>
                    </>
                  )}
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDeleteMaterial}
              >
                Yes, delete
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal fade ${showImportModal ? "show" : ""}`}
        style={{ display: showImportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalImportLrfqLabel"
        aria-hidden={!showImportModal}
        onClick={closeImportModal}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "520px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-content"
            style={{ borderRadius: "10px", overflow: "hidden" }}
          >
            <div
              className="modal-header"
              style={{
                backgroundColor: "#003366",
                color: "#fff",
                borderBottom: "none",
              }}
            >
              <h5 className="modal-title" id="modalImportLrfqLabel">
                <i className="fa-solid fa-file-excel mr-2" />
                Import Materials &amp; Lines
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={closeImportModal}
                disabled={isImporting}
                style={{ color: "#fff", opacity: 0.9 }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="modal-body" style={{ padding: "20px" }}>
              {/* Drop zone */}
              <input
                ref={importFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleImportFileSelect}
                disabled={isImporting}
              />

              {!importFile ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    !isImporting && importFileInputRef.current?.click()
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      importFileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                  }}
                  onDragLeave={() => setIsDragOver(false)}
                  onDrop={handleImportDrop}
                  style={{
                    border: `2px dashed ${isDragOver ? "#1199B2" : "#b8c7d6"}`,
                    background: isDragOver ? "#eaf7fa" : "#fbfdff",
                    borderRadius: "10px",
                    padding: "28px 16px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  <i
                    className="fa-solid fa-cloud-arrow-up"
                    style={{
                      fontSize: "34px",
                      color: "#1199B2",
                      marginBottom: "8px",
                    }}
                  />
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#003366",
                    }}
                  >
                    Drag &amp; drop your Excel file here
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: "12px", margin: "4px 0 12px" }}
                  >
                    or
                  </div>
                  <span className="btn btn-sm btn-blue">
                    <i className="fa-solid fa-folder-open mr-1" />
                    Choose File
                  </span>
                  <div className="text-muted mt-2" style={{ fontSize: "11px" }}>
                    Supported: .xlsx, .xls · Max 5 MB
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    border: "1px solid #c8e6c9",
                    background: "#f1f8f2",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    gap: "10px",
                  }}
                >
                  <div
                    className="d-flex align-items-center"
                    style={{ minWidth: 0, gap: "12px" }}
                  >
                    <i
                      className="fa-solid fa-file-excel"
                      style={{ fontSize: "28px", color: "#217346" }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          wordBreak: "break-word",
                        }}
                      >
                        {importFile.name}
                      </div>
                      <div className="text-muted" style={{ fontSize: "11px" }}>
                        {formatFileSize(importFile.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={removeImportFile}
                    disabled={isImporting}
                    title="Remove file"
                  >
                    <i className="fa-solid fa-trash-can" />
                  </button>
                </div>
              )}
            </div>

            <div
              className="modal-footer"
              style={{ borderTop: "1px solid #eef1f4" }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeImportModal}
                disabled={isImporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleImportSubmit}
                disabled={isImporting || !importFile}
              >
                {isImporting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin mr-1" />
                    Importing...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-import mr-1" />
                    Import
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showImportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default LogisticsRfqCreate;
